import React, { useState, useEffect, useMemo, useCallback, memo } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import LoadingSpinner from '../components/LoadingSpinner';
import './MyCoursesManager.css';

/** Opens course builder in a new tab; stops row click propagation in the course list. */
function CourseBuilderOpenIconLink({ courseId, canOpen, className = '' }) {
  if (!canOpen || courseId == null) return null;
  const label = 'Open in Course Builder (new tab)';
  return (
    <a
      href={`/course-builder?id=${courseId}`}
      target="_blank"
      rel="noopener noreferrer"
      className={`course-builder-open-link ${className}`.trim()}
      title={label}
      aria-label={label}
      onClick={(e) => e.stopPropagation()}
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth={1.75}
        strokeLinecap="round"
        strokeLinejoin="round"
        className="course-builder-open-icon"
        width={16}
        height={16}
        aria-hidden
      >
        <path d="M13.5 6H5.25A2.25 2.25 0 003 8.25v10.5A2.25 2.25 0 005.25 21h10.5A2.25 2.25 0 0018 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25" />
      </svg>
    </a>
  );
}

/**
 * Double-click X to mark a course status='Deleted'.
 * Single click is intentionally swallowed so the row's onClick (open students modal)
 * doesn't fire and so an accidental click can't delete.
 * Hidden entirely unless the user has the my-courses-manager.DeleteCourse capability.
 */
function CourseDeleteIconButton({ courseId, canDelete, isDeleting, onDelete, className = '' }) {
  if (!canDelete || courseId == null) return null;
  const label = isDeleting ? 'Deleting…' : 'Double-click to delete course';
  return (
    <button
      type="button"
      className={`course-delete-button ${className}`.trim()}
      title={label}
      aria-label={label}
      disabled={isDeleting}
      onClick={(e) => e.stopPropagation()}
      onDoubleClick={(e) => {
        e.stopPropagation();
        if (!isDeleting) onDelete(courseId);
      }}
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth={2}
        strokeLinecap="round"
        strokeLinejoin="round"
        className="course-delete-icon"
        width={14}
        height={14}
        aria-hidden
      >
        <path d="M18 6L6 18M6 6l12 12" />
      </svg>
    </button>
  );
}

// Memoized flag container - uses CSS show/hide for instant expand/collapse (like StudentHoursOverview panel)
const FlagContainer = memo(({ flaggedCount, flaggedCoursesWithStatus, canOpenCourseBuilder }) => {
  const [expanded, setExpanded] = useState(false);
  const toggle = useCallback(() => setExpanded(prev => !prev), []);

  if (flaggedCount === 0) return null;

  return (
    <div className={`my-courses-flag-container ${expanded ? 'expanded' : 'collapsed'} has-issues`}>
      <div className="flag-header" onClick={toggle} role="button" tabIndex={0} onKeyDown={(e) => e.key === 'Enter' && toggle()}>
        <div className="flag-header-left">
          <span className="flag-title">
            ⚠️ {flaggedCount} Course{flaggedCount !== 1 ? 's' : ''} Flagged
          </span>
          <span className="flag-toggle">{expanded ? '−' : '+'}</span>
        </div>
      </div>
      <div className="flag-content">
        <div className="deployment-status">
          <div className="issues-list">
            <div className="issues-header">
              {flaggedCount} course{flaggedCount !== 1 ? 's' : ''} {flaggedCount !== 1 ? 'have' : 'has'} been flagged and {flaggedCount !== 1 ? 'are' : 'is'} not ready for students to be enrolled:
            </div>
            <div className="issues-items">
              {flaggedCoursesWithStatus.map(({ course, status }) => (
                <div key={course.id} className="issue-item">
                  <div className="issue-title-row">
                    <CourseBuilderOpenIconLink courseId={course.id} canOpen={canOpenCourseBuilder} />
                    <span className="issue-path">{course.title || 'Untitled Course'}</span>
                  </div>
                  <div className="issue-details">
                    {status.issuesCount} issue{status.issuesCount !== 1 ? 's' : ''}: {status.issues.map(i => i.issueTypes.join(', ')).join('; ')}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
});

const HOUR_TYPE_TOOLTIP_ORDER = ['Theory', 'Lab', 'Field Experience', 'Concentration', 'Capstone'];

/** Creator display: admin/all-courses joins account; own-courses list may only have createdby + current user. */
function formatCourseCreatorLabel(course, userData) {
  const fn = (course.first_name || '').trim();
  const ln = (course.last_name || '').trim();
  const name = [fn, ln].filter(Boolean).join(' ');
  if (name) return name;
  if (course.primary_email) return course.primary_email;
  if (userData?.account_id && course.createdby && String(course.createdby) === String(userData.account_id)) {
    const selfName = [userData.first_name, userData.last_name].filter(Boolean).join(' ').trim();
    if (selfName) return selfName;
    if (userData.primary_email) return userData.primary_email;
  }
  return '—';
}

/** Multi-line title tooltip: hours summed by type from hours_earned arrays */
function buildHoursEarnedTooltip(hoursByType) {
  const entries = Object.entries(hoursByType).filter(([, v]) => v > 0);
  if (entries.length === 0) return '';
  const ordered = sortHourTypeEntries(entries);
  return `Hours by type:\n${ordered.map(([t, v]) => `${t}: ${Math.round(v * 10) / 10}h`).join('\n')}`;
}

function sortHourTypeEntries(entries) {
  return [...entries].sort((a, b) => {
    const ia = HOUR_TYPE_TOOLTIP_ORDER.indexOf(a[0]);
    const ib = HOUR_TYPE_TOOLTIP_ORDER.indexOf(b[0]);
    if (ia !== -1 || ib !== -1) {
      if (ia === -1) return 1;
      if (ib === -1) return -1;
      return ia - ib;
    }
    return a[0].localeCompare(b[0]);
  });
}

/** Hours from one submission's hours_earned (completed only; same parsing as roster progress). */
function getSubmissionHoursEarnedDetail(submission) {
  const empty = { totalHours: 0, hoursEarnedTooltip: '', hourEntries: [] };
  if (submission.status !== 'completed' || !submission.hours_earned) {
    return empty;
  }
  try {
    const earned =
      typeof submission.hours_earned === 'string'
        ? JSON.parse(submission.hours_earned)
        : submission.hours_earned;
    if (!Array.isArray(earned)) {
      return empty;
    }
    let totalHours = 0;
    const hoursByType = {};
    for (let j = 0; j < earned.length; j++) {
      const h = earned[j];
      const add = Number(h?.hours) || 0;
      totalHours += add;
      const typeKey = (h?.type && String(h.type).trim()) || 'Other';
      hoursByType[typeKey] = (hoursByType[typeKey] || 0) + add;
    }
    const hourEntries = sortHourTypeEntries(Object.entries(hoursByType).filter(([, v]) => v > 0));
    return {
      totalHours: Math.round(totalHours * 10) / 10,
      hoursEarnedTooltip: buildHoursEarnedTooltip(hoursByType),
      hourEntries
    };
  } catch (_) {
    return empty;
  }
}

/** Pure progress from pre-filtered submissions for one enrollment (O(subs), no full-array scans). */
function computeEnrollmentProgressFromSubs(submissionsForEnrollment, totalComponents, componentTypeById) {
  if (!totalComponents) {
    return {
      completed: 0,
      total: 0,
      percentage: 0,
      pending: 0,
      lastSubmission: null,
      totalHours: 0,
      hoursEarnedTooltip: ''
    };
  }

  let completedSubmissions = 0;
  let pendingSubmissions = 0;
  let lastSubmission = null;

  for (let i = 0; i < submissionsForEnrollment.length; i++) {
    const submission = submissionsForEnrollment[i];
    if (submission.status === 'completed') {
      completedSubmissions++;
    } else if (submission.status === 'submitted') {
      const type = componentTypeById.get(submission.component);
      if (type !== 'Discussion Board') pendingSubmissions++;
    }
    const d = submission.createddate;
    if (d && (!lastSubmission || new Date(d) > new Date(lastSubmission))) {
      lastSubmission = d;
    }
  }

  const percentage = totalComponents > 0 ? Math.round((completedSubmissions / totalComponents) * 100) : 0;

  let totalHours = 0;
  const hoursByType = {};
  for (let i = 0; i < submissionsForEnrollment.length; i++) {
    const submission = submissionsForEnrollment[i];
    if (submission.status !== 'completed' || !submission.hours_earned) continue;
    try {
      const earned =
        typeof submission.hours_earned === 'string'
          ? JSON.parse(submission.hours_earned)
          : submission.hours_earned;
      if (Array.isArray(earned)) {
        for (let j = 0; j < earned.length; j++) {
          const h = earned[j];
          const add = Number(h?.hours) || 0;
          totalHours += add;
          const typeKey = (h?.type && String(h.type).trim()) || 'Other';
          hoursByType[typeKey] = (hoursByType[typeKey] || 0) + add;
        }
      }
    } catch (_) {}
  }

  const hoursEarnedTooltip = buildHoursEarnedTooltip(hoursByType);

  return {
    completed: completedSubmissions,
    total: totalComponents,
    percentage,
    pending: pendingSubmissions,
    lastSubmission,
    totalHours: Math.round(totalHours * 10) / 10,
    hoursEarnedTooltip
  };
}

const MyCoursesManager = ({ userData }) => {
  const [searchParams] = useSearchParams();
  const [canOpenCourseBuilder, setCanOpenCourseBuilder] = useState(false);
  const [canDeleteCourse, setCanDeleteCourse] = useState(false);
  const [courses, setCourses] = useState([]);
  const [deletingCourseIds, setDeletingCourseIds] = useState(() => new Set());
  const [courseEnrollments, setCourseEnrollments] = useState([]);
  const [courseComponents, setCourseComponents] = useState({}); // courseId -> components array
  const [componentSubmissions, setComponentSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [criticalError, setCriticalError] = useState(false);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [showStudentsModal, setShowStudentsModal] = useState(false);
  const [students, setStudents] = useState([]);
  const [sortBy, setSortBy] = useState('progress'); // 'name', 'progress', 'hoursEarned', 'lastSubmission', 'pending', 'startDate', 'createddate'
  const [sortOrder, setSortOrder] = useState('desc'); // 'asc', 'desc'
  const [expandedStudent, setExpandedStudent] = useState(null); // Track which student row is expanded
  const [courseSortBy, setCourseSortBy] = useState('pending'); // 'title', 'students', 'pending', 'flags', 'offeredhours', 'createddate', 'creator'
  const [courseSortOrder, setCourseSortOrder] = useState('desc'); // 'asc', 'desc'
  const [showAllCourses, setShowAllCourses] = useState(false); // Track if showing all courses
  const [courseTypeFilter, setCourseTypeFilter] = useState('cte'); // 'all' | 'cte' | 'general'
  const [showColumns, setShowColumns] = useState({
    hours: true,
    students: true,
    review: true,
    flags: true,
    created: false,
    creator: false
  });
  // Helper function to get current date in YYYY-MM-DD format
  const getCurrentDate = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const [startDateFrom, setStartDateFrom] = useState(''); // Filter start date from
  const [startDateTo, setStartDateTo] = useState(''); // Filter end date (Cohort To); empty = no upper bound
  
  // Add Student modal state
  const [showAddStudentModal, setShowAddStudentModal] = useState(false);
  const [allStudents, setAllStudents] = useState([]);
  const [selectedStudentsToAdd, setSelectedStudentsToAdd] = useState([]);
  const [studentSearchTerm, setStudentSearchTerm] = useState('');
  const [courseGroups, setCourseGroups] = useState([]);
  const [selectedCourseGroup, setSelectedCourseGroup] = useState('');
  /** Optional enrollment window when adding to a standalone General (TK12) course (no course group). */
  const [addStudentStartDate, setAddStudentStartDate] = useState('');
  const [addStudentEndDate, setAddStudentEndDate] = useState('');

  // Copy Roster modal state
  const [showCopyRosterModal, setShowCopyRosterModal] = useState(false);
  const [selectedStudentsToCopy, setSelectedStudentsToCopy] = useState([]);
  const [targetCourseId, setTargetCourseId] = useState('');
  const [copyStartDate, setCopyStartDate] = useState('');
  const [copyEndDate, setCopyEndDate] = useState('');
  const [setPreviousCourse, setSetPreviousCourse] = useState(true); // Default checked

  // Withdrawal modal state
  const [showWithdrawalModal, setShowWithdrawalModal] = useState(false);
  const [studentToWithdraw, setStudentToWithdraw] = useState(null);

  // Check if this is full access mode
  const isFullAccess = searchParams.get('command') === 'full-access';
  const deletionsAllowed = searchParams.get('deletions') === 'allowed';
  const withdrawalsAllowed = searchParams.get('withdrawals') === 'allowed';
  const copyRosterAllowed = searchParams.get('copyRoster') === 'allowed';
  const copyEmailsAllowed = searchParams.get('copyEmails') === 'allowed';

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get('/api/check-permission?pagePath=my-courses-manager', {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (!cancelled) {
          setCanOpenCourseBuilder(Boolean(res.data.capabilities?.OpenCourseBuilder));
          setCanDeleteCourse(Boolean(res.data.capabilities?.DeleteCourse));
        }
      } catch {
        if (!cancelled) {
          setCanOpenCourseBuilder(false);
          setCanDeleteCourse(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    loadData();
  }, [userData, isFullAccess, showAllCourses]);

  // Auto-select course group when Add Student modal opens
  useEffect(() => {
    if (showAddStudentModal && selectedCourse && courseGroups.length > 0 && !selectedCourseGroup) {
      // Find course groups that contain the current course
      const groupsWithCourse = courseGroups.filter(
        group => group.courses && group.courses.includes(selectedCourse.id)
      );

      if (groupsWithCourse.length > 0) {
        // Find the group where the current course is the first course in the setup/wheel
        const groupWithFirstCourse = groupsWithCourse.find(group => {
          if (group.setup && Array.isArray(group.setup) && group.setup.length > 0) {
            // Check if the first course in setup matches the current course
            return group.setup[0].course_id === selectedCourse.id;
          }
          return false;
        });

        // If found, select it; otherwise select the first group that contains the course
        if (groupWithFirstCourse) {
          setSelectedCourseGroup(groupWithFirstCourse.id);
        } else if (groupsWithCourse.length === 1) {
          // If only one group contains the course, select it
          setSelectedCourseGroup(groupsWithCourse[0].id);
        } else if (groupsWithCourse.length > 1) {
          // If multiple groups but none has this course as first, select the first one
          setSelectedCourseGroup(groupsWithCourse[0].id);
        }
      }
    }
  }, [showAddStudentModal, selectedCourse, courseGroups, selectedCourseGroup]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError('');
      setCriticalError(false);

      const token = localStorage.getItem('token');
      if (!token) {
        setError('No authentication token found. Please log in.');
        setCriticalError(true);
        return;
      }

      // Track query success/failure
      const queryResults = {
        courses: false,
        students: false,
        enrollments: false,
        components: false,
        submissions: false,
        courseGroups: false
      };

      // Load courses, students, and course groups in parallel
      try {
        // Handle courses loading with fallback for admin endpoint
        let coursesResponse;
        if (isFullAccess || showAllCourses) {
          try {
            // Try admin endpoint first
            coursesResponse = await axios.get('/api/components/admin/all-courses', { headers: { Authorization: `Bearer ${token}` } });
          } catch (adminError) {
            // If admin endpoint fails (e.g., access denied), fall back to regular endpoint
            console.log('Admin endpoint failed, falling back to regular endpoint:', adminError.response?.data?.error || adminError.message);
            coursesResponse = await axios.get(`/api/components?type=Course&createdby=${userData.account_id}`, { headers: { Authorization: `Bearer ${token}` } });
          }
        } else {
          coursesResponse = await axios.get(`/api/components?type=Course&createdby=${userData.account_id}`, { headers: { Authorization: `Bearer ${token}` } });
        }

        // Load students and course groups in parallel
        const [studentsResponse, courseGroupsResponse] = await Promise.all([
          axios.get('/api/course-enrollments/manager-data', { headers: { Authorization: `Bearer ${token}` } }),
          axios.get('/api/course-groups', { headers: { Authorization: `Bearer ${token}` } })
        ]);

        // Load courses based on access mode
        let courses;
        if (isFullAccess || showAllCourses) {
          // Check if response has courses (admin endpoint) or components (regular endpoint)
          courses = coursesResponse.data.courses || coursesResponse.data.components || [];
        } else {
          courses = coursesResponse.data.components || [];
        }

        // Load all students for the add student modal
        setAllStudents(studentsResponse.data.students || []);

        // Load course groups
        setCourseGroups(courseGroupsResponse.data || []);

        // Filter to only Active courses
        courses = courses.filter(course => course.status === 'Active');

        setCourses(courses);
        queryResults.courses = true;
        queryResults.students = true;
        queryResults.courseGroups = true;

        // Only load enrollments and components if we have courses
        if (courses.length > 0) {
          const courseIds = courses.map(course => course.id);
          
          // Load course enrollments for only these specific courses
          try {
            const enrollmentsResponse = await axios.get('/api/course-enrollments/by-courses', {
              params: { courseIds: JSON.stringify(courseIds) },
              headers: { Authorization: `Bearer ${token}` }
            });

            const enrollments = enrollmentsResponse.data.courseEnrollments || [];
            setCourseEnrollments(enrollments);
            queryResults.enrollments = true;

            // Load components efficiently based on access mode
            const componentsMap = {};
            let componentsSuccess = true;
            
            if (isFullAccess && courses.length > 5) {
              // Use bulk endpoint for full access with many courses
              try {
                const courseIds = courses.map(course => course.id);
                const bulkResponse = await axios.post('/api/components/bulk-viewer', {
                  courseIds: courseIds
                }, {
                  headers: { Authorization: `Bearer ${token}` }
                });
                
                // Process bulk response
                bulkResponse.data.courses.forEach(courseData => {
                  const contentComponents = courseData.components.filter(component => 
                    ['Content', 'Discussion Board', 'Assignment'].includes(component.type)
                  );
                  componentsMap[courseData.course.id] = contentComponents;
                });
                
                console.log(`✅ Loaded components for ${courses.length} courses via bulk endpoint`);
              } catch (error) {
                console.error('Error loading components via bulk endpoint:', error);
                componentsSuccess = false;
                // Fallback to individual requests
                console.log('Falling back to individual component requests...');
                
                const componentPromises = courses.map(async (course) => {
                  try {
                    const viewerResponse = await axios.get(`/api/components/viewer/${course.id}`, {
                      headers: { Authorization: `Bearer ${token}` }
                    });
                    
                    if (viewerResponse.data.components) {
                      const contentComponents = viewerResponse.data.components.filter(component => 
                        ['Content', 'Discussion Board', 'Assignment'].includes(component.type)
                      );
                      return { courseId: course.id, components: contentComponents, success: true };
                    } else {
                      return { courseId: course.id, components: [], success: true };
                    }
                  } catch (error) {
                    console.error(`Error loading components for course ${course.id}:`, error);
                    return { courseId: course.id, components: [], success: false };
                  }
                });

                const componentResults = await Promise.all(componentPromises);
                componentResults.forEach(result => {
                  componentsMap[result.courseId] = result.components;
                  if (!result.success) {
                    componentsSuccess = false;
                  }
                });
              }
            } else {
              // Use parallel individual requests for limited access or few courses
              const componentPromises = courses.map(async (course) => {
                try {
                  const viewerResponse = await axios.get(`/api/components/viewer/${course.id}`, {
                    headers: { Authorization: `Bearer ${token}` }
                  });
                  
                  if (viewerResponse.data.components) {
                    const contentComponents = viewerResponse.data.components.filter(component => 
                      ['Content', 'Discussion Board', 'Assignment'].includes(component.type)
                    );
                    return { courseId: course.id, components: contentComponents, success: true };
                  } else {
                    return { courseId: course.id, components: [], success: true };
                  }
                } catch (error) {
                  console.error(`Error loading components for course ${course.id}:`, error);
                  return { courseId: course.id, components: [], success: false };
                }
              });

              const componentResults = await Promise.all(componentPromises);
              componentResults.forEach(result => {
                componentsMap[result.courseId] = result.components;
                if (!result.success) {
                  componentsSuccess = false;
                }
              });
            }
            
            setCourseComponents(componentsMap);
            queryResults.components = componentsSuccess;

            // Load component submissions for all course enrollments
            if (enrollments.length > 0) {
              const enrollmentIds = enrollments.map(enrollment => enrollment.id);
              
              try {
                // Use POST request to avoid URL length limits with large enrollment arrays
                const submissionsResponse = await axios.post('/api/component-submissions/bulk', {
                  course_enrollments: enrollmentIds
                }, {
                  headers: { Authorization: `Bearer ${token}` }
                });
                
                setComponentSubmissions(submissionsResponse.data.componentSubmissions || []);
                queryResults.submissions = true;
              } catch (error) {
                console.error('Error loading component submissions:', error);
                setComponentSubmissions([]);
                queryResults.submissions = false;
              }
            } else {
              setComponentSubmissions([]);
              queryResults.submissions = true; // No enrollments is not an error
            }
          } catch (error) {
            console.error('Error loading course enrollments:', error);
            queryResults.enrollments = false;
            setCourseEnrollments([]);
            setCourseComponents({});
            setComponentSubmissions([]);
          }
        } else {
          setCourseEnrollments([]);
          setCourseComponents({});
          setComponentSubmissions([]);
          queryResults.enrollments = true;
          queryResults.components = true;
          queryResults.submissions = true; // No courses is not an error
        }

        // Check if any critical queries failed
        const failedQueries = Object.entries(queryResults)
          .filter(([query, success]) => !success)
          .map(([query]) => query);

        if (failedQueries.length > 0) {
          console.error('Critical queries failed:', failedQueries);
          setCriticalError(true);
          setError(`Critical data loading failed. Failed queries: ${failedQueries.join(', ')}`);
          return;
        }

      } catch (error) {
        console.error('Error in initial parallel queries:', error);
        setCriticalError(true);
        setError(`Critical error loading initial data: ${error.response?.data?.error || error.message}`);
        return;
      }

    } catch (error) {
      console.error('Unexpected error loading data:', error);
      setCriticalError(true);
      setError(`Unexpected error: ${error.response?.data?.error || error.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Courses that allow enrollment (modlock must be true)
  const enrollableCourses = React.useMemo(() =>
    courses.filter(c => c.modlock === true),
    [courses]
  );

  // Check if a course group has any course that does not allow enrollment (modlock !== true)
  const groupHasUnlockedCourses = (group) => {
    const groupCourseIds = group?.courses || (group?.courses_with_dates || []).map(cd => cd.id || cd.course_id).filter(Boolean);
    return groupCourseIds.some(cid => {
      const c = courses.find(cf => cf.id === cid);
      return !c || c.modlock !== true;
    });
  };

  // Deployment readiness: check Content, Discussion Board, Assignment for hourawards and grade_settings
  const getDeploymentReadinessForCourse = (courseId) => {
    const components = courseComponents[courseId] || [];
    const targetTypes = ['Content', 'Discussion Board', 'Assignment'];
    const issues = [];

    const isGradeSettingsEmpty = (gs) => {
      if (!gs) return true;
      if (typeof gs === 'object' && Object.keys(gs).length === 0) return true;
      return false;
    };

    components.forEach((comp) => {
      if (!targetTypes.includes(comp.type)) return;
      let hourawards = comp.hourawards;
      if (typeof hourawards === 'string') {
        try { hourawards = hourawards ? JSON.parse(hourawards) : []; } catch { hourawards = []; }
      }
      const hasHourAwardsIssue = !hourawards || !Array.isArray(hourawards) || hourawards.length === 0;
      const gradeSettings = comp.grade_settings || comp.gradeSettings;
      const hasGradeSettingsIssue = isGradeSettingsEmpty(gradeSettings);

      if (hasHourAwardsIssue || hasGradeSettingsIssue) {
        const issueTypes = [];
        if (hasHourAwardsIssue) issueTypes.push('hour awards');
        if (hasGradeSettingsIssue) issueTypes.push('grade settings');
        issues.push({
          componentType: comp.type,
          title: comp.title || comp.text || `Untitled ${comp.type}`,
          issueTypes
        });
      }
    });

    return { isReady: issues.length === 0, issuesCount: issues.length, issues };
  };

  const getFlagsCount = (courseId) => getDeploymentReadinessForCourse(courseId).issuesCount;

  const getFlaggedCoursesCount = () => courses.filter(c => getFlagsCount(c.id) > 0).length;

  // Compute total hours and breakdown by type from course components (matches CourseBuilder display)
  const getCourseHoursBreakdown = (courseId) => {
    const types = ['Theory', 'Lab', 'Field Experience', 'Concentration', 'Capstone'];
    const breakdown = { Theory: 0, Lab: 0, 'Field Experience': 0, Concentration: 0, Capstone: 0 };
    const components = courseComponents[courseId] || [];
    components.forEach((comp) => {
      let ha = comp.hourawards;
      if (typeof ha === 'string') {
        try { ha = ha ? JSON.parse(ha) : []; } catch { ha = []; }
      }
      if (Array.isArray(ha)) {
        ha.forEach((a) => {
          const t = a.type || 'Theory';
          const h = typeof a.hours === 'number' ? a.hours : (parseFloat(a.hours) || 0);
          if (breakdown[t] !== undefined) breakdown[t] += h;
          else breakdown.Theory += h;
        });
      }
    });
    const total = types.reduce((sum, t) => sum + breakdown[t], 0);
    return { total, breakdown, types };
  };

  // Index submissions once; progress per enrollment is O(subs for that row), not O(all submissions × enrollments)
  const enrollmentProgressById = useMemo(() => {
    const map = new Map();
    const submissionsByEnrollment = new Map();
    for (let i = 0; i < componentSubmissions.length; i++) {
      const s = componentSubmissions[i];
      const ce = s.courseenrollment;
      if (!submissionsByEnrollment.has(ce)) submissionsByEnrollment.set(ce, []);
      submissionsByEnrollment.get(ce).push(s);
    }
    for (let i = 0; i < courseEnrollments.length; i++) {
      const e = courseEnrollments[i];
      if (e.status !== 'enrolled') continue;
      const comps = courseComponents[e.course] || [];
      const typeMap = new Map();
      for (let j = 0; j < comps.length; j++) {
        const c = comps[j];
        typeMap.set(c.id, c.type);
      }
      const subs = submissionsByEnrollment.get(e.id) || [];
      map.set(e.id, computeEnrollmentProgressFromSubs(subs, comps.length, typeMap));
    }
    return map;
  }, [courseEnrollments, courseComponents, componentSubmissions]);

  const courseMetricsByCourseId = useMemo(() => {
    const m = {};
    for (let ci = 0; ci < courses.length; ci++) {
      const course = courses[ci];
      const cid = course.id;
      let pendingSum = 0;
      const enrolls = [];
      for (let i = 0; i < courseEnrollments.length; i++) {
        const e = courseEnrollments[i];
        if (e.course !== cid || e.status !== 'enrolled') continue;
        enrolls.push(e);
        const p = enrollmentProgressById.get(e.id);
        if (p) pendingSum += p.pending;
      }
      m[cid] = {
        enrolledCount: enrolls.length,
        pendingSum,
        flagsCount: getDeploymentReadinessForCourse(cid).issuesCount,
        hoursBreakdown: getCourseHoursBreakdown(cid)
      };
    }
    return m;
  }, [courses, courseEnrollments, enrollmentProgressById, courseComponents]);

  const sortedCourseList = useMemo(() => {
    const metrics = courseMetricsByCourseId;
    return [...courses].sort((a, b) => {
      let aValue;
      let bValue;
      switch (courseSortBy) {
        case 'title':
          aValue = (a.title || '').toLowerCase();
          bValue = (b.title || '').toLowerCase();
          break;
        case 'students':
          aValue = metrics[a.id]?.enrolledCount ?? 0;
          bValue = metrics[b.id]?.enrolledCount ?? 0;
          break;
        case 'pending':
          aValue = metrics[a.id]?.pendingSum ?? 0;
          bValue = metrics[b.id]?.pendingSum ?? 0;
          break;
        case 'flags':
          aValue = metrics[a.id]?.flagsCount ?? 0;
          bValue = metrics[b.id]?.flagsCount ?? 0;
          break;
        case 'createddate':
          aValue = new Date(a.createddate || 0);
          bValue = new Date(b.createddate || 0);
          break;
        case 'creator':
          aValue = formatCourseCreatorLabel(a, userData).toLowerCase();
          bValue = formatCourseCreatorLabel(b, userData).toLowerCase();
          break;
        case 'offeredhours':
          aValue = metrics[a.id]?.hoursBreakdown?.total ?? 0;
          bValue = metrics[b.id]?.hoursBreakdown?.total ?? 0;
          break;
        default:
          return 0;
      }
      if (courseSortOrder === 'asc') {
        return aValue > bValue ? 1 : aValue < bValue ? -1 : 0;
      }
      return aValue < bValue ? 1 : aValue > bValue ? -1 : 0;
    });
  }, [courses, courseSortBy, courseSortOrder, courseMetricsByCourseId, userData]);

  const filteredCourseList = useMemo(() => {
    // Match CourseBuilder behavior: TK12 is General; everything else is treated as CTE.
    const isGeneral = (course) => course?.course_track === 'TK12';
    if (courseTypeFilter === 'all') return sortedCourseList;
    if (courseTypeFilter === 'general') return sortedCourseList.filter((c) => isGeneral(c));
    // Default CTE includes explicit CTE and legacy rows without course_track.
    return sortedCourseList.filter((c) => !isGeneral(c));
  }, [sortedCourseList, courseTypeFilter]);

  const courseGroupsContainingSelected = useMemo(() => {
    if (!selectedCourse?.id) return [];
    return courseGroups.filter((g) => g.courses && g.courses.includes(selectedCourse.id));
  }, [courseGroups, selectedCourse?.id]);

  /** General (TK12) course with no course group: enroll into this course only, optional dates/period. */
  const addStudentsSingleCourseMode = useMemo(
    () => selectedCourse?.course_track === 'TK12' && courseGroupsContainingSelected.length === 0,
    [selectedCourse?.course_track, courseGroupsContainingSelected.length]
  );

  const coursesWithItemsToReviewCount = useMemo(() => {
    let n = 0;
    for (let i = 0; i < courses.length; i++) {
      if ((courseMetricsByCourseId[courses[i].id]?.pendingSum ?? 0) > 0) n++;
    }
    return n;
  }, [courses, courseMetricsByCourseId]);

  const calculateProgress = (courseEnrollmentId, courseId) => {
    const cached = enrollmentProgressById.get(courseEnrollmentId);
    if (cached) return cached;
    const total = courseComponents[courseId]?.length || 0;
    return {
      completed: 0,
      total,
      percentage: 0,
      pending: 0,
      lastSubmission: null,
      totalHours: 0,
      hoursEarnedTooltip: ''
    };
  };

  const selectedCourseOfferedHours = useMemo(() => {
    if (!selectedCourse) return null;
    return getCourseHoursBreakdown(selectedCourse.id);
  }, [selectedCourse, courseComponents]);

  // Memoize flagged courses list - avoids expensive recompute on every expand/collapse (like StudentHoursOverview)
  const flaggedCoursesWithStatus = useMemo(() => {
    return courses
      .filter(c => getFlagsCount(c.id) > 0)
      .sort((a, b) => getFlagsCount(b.id) - getFlagsCount(a.id))
      .map(course => ({ course, status: getDeploymentReadinessForCourse(course.id) }));
  }, [courses, courseComponents]);

  const flaggedCount = flaggedCoursesWithStatus.length;

  /**
   * Soft-delete a course by flipping its status to 'Deleted'. The course list is
   * filtered to status === 'Active' on load, so the row will disappear immediately
   * after the local courses state is updated.
   */
  const handleDeleteCourse = async (courseId) => {
    if (!courseId) return;
    if (!canDeleteCourse) return;
    if (deletingCourseIds.has(courseId)) return;

    setDeletingCourseIds(prev => {
      const next = new Set(prev);
      next.add(courseId);
      return next;
    });

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setError('No authentication token found. Please log in.');
        return;
      }

      await axios.put(
        `/api/components/${courseId}`,
        { status: 'Deleted' },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setCourses(prev => prev.filter(c => c.id !== courseId));
      if (selectedCourse?.id === courseId) {
        setShowStudentsModal(false);
        setSelectedCourse(null);
      }
    } catch (err) {
      console.error('Error deleting course:', err);
      setError(err.response?.data?.error || 'Failed to delete course');
    } finally {
      setDeletingCourseIds(prev => {
        const next = new Set(prev);
        next.delete(courseId);
        return next;
      });
    }
  };

  const handleCourseClick = async (course) => {
    try {
      setSelectedCourse(course);
      
      // Get students enrolled in this course with progress data
      const courseStudents = courseEnrollments
        .filter(enrollment => enrollment.course === course.id && enrollment.status === 'enrolled')
        .map(enrollment => {
          const progress = calculateProgress(enrollment.id, course.id);
          // Find the course group for this enrollment
          const courseGroup = courseGroups.find(group => group.id === enrollment.coursegroup);
          return {
            id: enrollment.id,
            enrollment_id: enrollment.enrollment,
            first_name: enrollment.first_name,
            last_name: enrollment.last_name,
            primary_email: enrollment.primary_email,
            start_date: enrollment.start_date,
            createddate: enrollment.createddate,
            coursegroup_title: courseGroup?.title || null,
            coursegroup_id: enrollment.coursegroup || null,
            progress: progress
          };
        });

      setStudents(courseStudents);
      setStartDateFrom(''); // Reset date filters when opening modal
      setStartDateTo(''); // Cohort To: cleared by default
      setShowStudentsModal(true);
    } catch (error) {
      console.error('Error loading students:', error);
      setError('Failed to load students for this course');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString();
  };

  const formatDateOnly = (dateString) => {
    if (!dateString) return 'N/A';
    // Handle date strings in YYYY-MM-DD format to avoid timezone issues
    // Parse the date string directly to avoid timezone conversion
    const dateParts = dateString.split('T')[0].split('-');
    if (dateParts.length === 3) {
      const year = dateParts[0];
      const month = dateParts[1];
      const day = dateParts[2];
      // Create date in local timezone to avoid shifting
      const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
      return date.toLocaleDateString();
    }
    // Fallback to standard parsing if format is different
    return new Date(dateString).toLocaleDateString();
  };

  // Helper function to get color based on first two letters of course group title
  const getCourseGroupColor = (title) => {
    if (!title || title.length < 2) return '#6b7280'; // Default grey
    
    const prefix = title.substring(0, 2).toUpperCase();
    switch (prefix) {
      case 'DM':
        return '#f97316'; // Orange
      case 'RT':
        return '#6b7280'; // Grey
      case 'MB':
        return '#22c55e'; // Green
      case 'PH':
        return '#a855f7'; // Purple
      case 'GD':
        return '#14b8a6'; // Teal
      case 'VP':
        return '#eab308'; // Yellow
      default:
        return '#6b7280'; // Default grey
    }
  };

  const getStudentComponentSubmissions = (studentId) => {
    return componentSubmissions.filter(submission => 
      submission.courseenrollment === studentId
    ).sort((a, b) => {
      // Find components to determine types
      const componentA = courseComponents[selectedCourse.id]?.find(comp => comp.id === a.component);
      const componentB = courseComponents[selectedCourse.id]?.find(comp => comp.id === b.component);
      
      // Priority 1: Assignment submitted (requires review) - highest priority
      if (a.status === 'submitted' && componentA?.type === 'Assignment' && 
          !(b.status === 'submitted' && componentB?.type === 'Assignment')) return -1;
      if (b.status === 'submitted' && componentB?.type === 'Assignment' && 
          !(a.status === 'submitted' && componentA?.type === 'Assignment')) return 1;
      
      // Priority 2: Other submitted items
      if (a.status === 'submitted' && b.status !== 'submitted') return -1;
      if (b.status === 'submitted' && a.status !== 'submitted') return 1;
      
      // Priority 3: Sort by date (most recent first)
      return new Date(b.createddate) - new Date(a.createddate);
    });
  };

  const handleStudentRowClick = (studentId) => {
    setExpandedStudent(expandedStudent === studentId ? null : studentId);
  };

  const handleComponentSubmissionClick = (submissionId) => {
    window.open(`/componentsubmissions?command=grade&id=${submissionId}`, '_blank');
  };

  const handleRemoveEnrollment = async (student, e) => {
    e.stopPropagation();
    if (!selectedCourse) return;
    if (!window.confirm(`Remove ${student.first_name} ${student.last_name} from this course?`)) return;

    try {
      const token = localStorage.getItem('token');

      // Resolve real course enrollment id (handle temp ids)
      let courseEnrollmentId = student.id;
      if (typeof courseEnrollmentId === 'string' && courseEnrollmentId.startsWith('temp-')) {
        const match = courseEnrollments.find(ce => ce.enrollment === student.enrollment_id && ce.course === selectedCourse.id);
        if (match) {
          courseEnrollmentId = match.id;
        } else {
          // No server record found; just remove locally
          setStudents(prev => prev.filter(s => s.id !== student.id));
          setCourseEnrollments(prev => prev.filter(ce => !(ce.enrollment === student.enrollment_id && ce.course === selectedCourse.id)));
          setComponentSubmissions(prev => prev.filter(cs => cs.courseenrollment !== student.id));
          return;
        }
      }

      await axios.delete(`/api/course-enrollments/${courseEnrollmentId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Update UI state
      setStudents(prev => prev.filter(s => s.id !== student.id && s.id !== courseEnrollmentId));
      setCourseEnrollments(prev => prev.filter(ce => ce.id !== courseEnrollmentId));
      setComponentSubmissions(prev => prev.filter(cs => cs.courseenrollment !== courseEnrollmentId));
    } catch (err) {
      console.error('Error removing course enrollment:', err);
      alert('Failed to remove student from course.');
    }
  };

  const handleWithdrawEnrollment = async () => {
    if (!selectedCourse || !studentToWithdraw) return;

    try {
      const token = localStorage.getItem('token');

      // Resolve real course enrollment id (handle temp ids)
      let courseEnrollmentId = studentToWithdraw.id;
      if (typeof courseEnrollmentId === 'string' && courseEnrollmentId.startsWith('temp-')) {
        const match = courseEnrollments.find(ce => ce.enrollment === studentToWithdraw.enrollment_id && ce.course === selectedCourse.id);
        if (match) {
          courseEnrollmentId = match.id;
        } else {
          alert('Failed to find enrollment record.');
          setShowWithdrawalModal(false);
          setStudentToWithdraw(null);
          return;
        }
      }

      // If student has a coursegroup, withdraw from all courses in that group
      if (studentToWithdraw.coursegroup_id) {
        // Get all enrollments for this student in this coursegroup
        const enrollmentsInGroup = courseEnrollments.filter(
          ce => ce.enrollment === studentToWithdraw.enrollment_id && ce.coursegroup === studentToWithdraw.coursegroup_id
        );

        // Update all enrollments to withdrawn status
        const updatePromises = enrollmentsInGroup.map(enrollment =>
          axios.put(`/api/course-enrollments/${enrollment.id}/status`, {
            status: 'withdrawn'
          }, {
            headers: { Authorization: `Bearer ${token}` }
          })
        );

        await Promise.all(updatePromises);

        // Update UI state - remove all affected enrollments from the arrays
        const enrollmentIdsToRemove = new Set(enrollmentsInGroup.map(e => e.id));
        setCourseEnrollments(prev => prev.filter(ce => !enrollmentIdsToRemove.has(ce.id)));

        // Remove student from students list (vice versa of adding when enrolling)
        setStudents(prev => prev.filter(s => !enrollmentIdsToRemove.has(s.id)));
      } else {
        // No coursegroup, just withdraw from this course
        await axios.put(`/api/course-enrollments/${courseEnrollmentId}/status`, {
          status: 'withdrawn'
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });

        // Update UI state - remove enrollment and student from arrays (vice versa of adding when enrolling)
        setCourseEnrollments(prev => prev.filter(ce => ce.id !== courseEnrollmentId));
        setStudents(prev => prev.filter(s => s.id !== courseEnrollmentId));
      }

      setShowWithdrawalModal(false);
      setStudentToWithdraw(null);
      alert('Student has been withdrawn from the course(s).');
    } catch (err) {
      console.error('Error withdrawing student:', err);
      alert('Failed to withdraw student from course.');
    }
  };

  const handleOpenWithdrawalModal = (student, e) => {
    e.stopPropagation();
    setStudentToWithdraw(student);
    setShowWithdrawalModal(true);
  };

  const filterStudentsByDateRange = (students, fromDate, toDate) => {
    if (!fromDate && !toDate) {
      return students; // No filter applied
    }

    return students.filter(student => {
      if (!student.start_date) {
        // If student has no start_date, include them only if both dates are empty
        // Otherwise exclude them
        return !fromDate && !toDate;
      }

      const studentDate = new Date(student.start_date);
      studentDate.setHours(0, 0, 0, 0); // Reset time to start of day for comparison

      // Check from date (null means beginning of time, so always pass)
      if (fromDate) {
        const from = new Date(fromDate);
        from.setHours(0, 0, 0, 0);
        if (studentDate < from) {
          return false;
        }
      }

      // Check to date (null means forever, so always pass)
      if (toDate) {
        const to = new Date(toDate);
        to.setHours(23, 59, 59, 999); // End of day
        if (studentDate > to) {
          return false;
        }
      }

      return true;
    });
  };

  const sortStudents = (students, sortBy, sortOrder) => {
    return [...students].sort((a, b) => {
      let aValue, bValue;
      
      switch (sortBy) {
        case 'name':
          aValue = `${a.first_name} ${a.last_name}`.toLowerCase();
          bValue = `${b.first_name} ${b.last_name}`.toLowerCase();
          break;
        case 'progress':
          aValue = a.progress.percentage;
          bValue = b.progress.percentage;
          break;
        case 'hoursEarned':
          aValue = Number(a.progress.totalHours) || 0;
          bValue = Number(b.progress.totalHours) || 0;
          break;
        case 'lastSubmission':
          aValue = a.progress.lastSubmission ? new Date(a.progress.lastSubmission) : new Date(0);
          bValue = b.progress.lastSubmission ? new Date(b.progress.lastSubmission) : new Date(0);
          break;
        case 'pending':
          aValue = a.progress.pending;
          bValue = b.progress.pending;
          break;
        case 'startDate':
          aValue = a.start_date ? new Date(a.start_date) : new Date(0);
          bValue = b.start_date ? new Date(b.start_date) : new Date(0);
          break;
        case 'createddate':
          aValue = a.createddate ? new Date(a.createddate) : new Date(0);
          bValue = b.createddate ? new Date(b.createddate) : new Date(0);
          break;
        default:
          return 0;
      }
      
      if (sortOrder === 'asc') {
        return aValue > bValue ? 1 : aValue < bValue ? -1 : 0;
      } else {
        return aValue < bValue ? 1 : aValue > bValue ? -1 : 0;
      }
    });
  };

  const downloadCSV = () => {
    // Get the filtered and sorted students (same as what's shown in the table)
    const filteredStudents = filterStudentsByDateRange(students, startDateFrom, startDateTo);
    const sortedStudents = sortStudents(filteredStudents, sortBy, sortOrder);
    
    if (sortedStudents.length === 0) {
      alert('No student data to export');
      return;
    }

    const isK12GeneralCourse = selectedCourse?.course_track === 'TK12';
    const headers = isK12GeneralCourse
      ? ['Name', 'Email', 'Last Submission', 'Completed', 'Total Components', 'Progress %', 'Hours Earned', 'Pending Items', 'Added at']
      : ['Name', 'Email', 'Last Submission', 'Completed', 'Total Components', 'Progress %', 'Hours Earned', 'Pending Items', 'Added at', 'Wheel'];

    const csvData = sortedStudents.map((student) => {
      const row = [
        `"${student.first_name} ${student.last_name}"`,
        `"${student.primary_email || 'N/A'}"`,
        `"${student.progress.lastSubmission ? formatDate(student.progress.lastSubmission) : 'No submissions'}"`,
        student.progress.completed,
        student.progress.total,
        student.progress.percentage,
        student.progress.totalHours ?? 0,
        student.progress.pending,
        `"${student.createddate ? formatDate(student.createddate) : 'N/A'}"`
      ];
      if (!isK12GeneralCourse) {
        row.push(`"${student.coursegroup_title || 'N/A'}"`);
      }
      return row;
    });

    // Combine headers and data
    const csvContent = [headers.join(','), ...csvData.map(row => row.join(','))].join('\n');

    // Create and download file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `${selectedCourse?.title || 'course'}_student_progress_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const copyEmailsToClipboard = async () => {
    // Get the filtered and sorted students (same as what's shown in the table)
    const filteredStudents = filterStudentsByDateRange(students, startDateFrom, startDateTo);
    const sortedStudents = sortStudents(filteredStudents, sortBy, sortOrder);
    
    if (sortedStudents.length === 0) {
      alert('No student emails to copy');
      return;
    }

    // Collect emails from filtered and sorted students, filtering out empty/null emails
    const emails = sortedStudents
      .map(student => student.primary_email)
      .filter(email => email && email.trim() !== '')
      .join('\n');

    if (emails.length === 0) {
      alert('No student emails available to copy');
      return;
    }

    const emailCount = sortedStudents.filter(s => s.primary_email && s.primary_email.trim() !== '').length;

    try {
      await navigator.clipboard.writeText(emails);
      alert(`Copied ${emailCount} email(s) to clipboard`);
    } catch (error) {
      console.error('Failed to copy emails to clipboard:', error);
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = emails;
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      document.body.appendChild(textArea);
      textArea.select();
      try {
        document.execCommand('copy');
        alert(`Copied ${emailCount} email(s) to clipboard`);
      } catch (err) {
        alert('Failed to copy emails to clipboard. Please try again.');
      }
      document.body.removeChild(textArea);
    }
  };

  // Handle adding students to course
  const handleAddStudents = async () => {
    if (selectedStudentsToAdd.length === 0 || !selectedCourse) {
      alert('Please select students to add to the course');
      return;
    }

    if (selectedCourse.modlock !== true) {
      alert('Enrollment is not allowed for this course because it is not locked for modifications.');
      return;
    }

    const hasCourseGroupForCourse = courseGroups.some(
      (g) => g.courses && g.courses.includes(selectedCourse.id)
    );
    const singleCourseEnrollmentMode =
      selectedCourse.course_track === 'TK12' && !hasCourseGroupForCourse;

    if (!singleCourseEnrollmentMode) {
      if (!selectedCourseGroup) {
        alert('Please select a course group for the students');
        return;
      }
      const selectedGroupCheck = courseGroups.find((group) => group.id === selectedCourseGroup);
      if (selectedGroupCheck && groupHasUnlockedCourses(selectedGroupCheck)) {
        alert(
          'Enrollment is not allowed for one or more courses in this group because they are not locked for modifications.'
        );
        return;
      }
    }

    let startDate = null;
    let endDateRaw = null;
    if (singleCourseEnrollmentMode) {
      startDate = addStudentStartDate?.trim() ? addStudentStartDate.trim() : null;
      endDateRaw = addStudentEndDate?.trim() ? addStudentEndDate.trim() : null;
      if (endDateRaw && startDate && endDateRaw < startDate) {
        alert('End date cannot be before start date.');
        return;
      }
    }

    try {
      const token = localStorage.getItem('token');

      if (singleCourseEnrollmentMode) {
        const enrollmentPromises = [];
        const enrollmentUpdates = [];
        const newEnrollmentsToAdd = [];

        for (const studentId of selectedStudentsToAdd) {
          const existingEnrollment = courseEnrollments.find(
            (ce) => ce.enrollment === studentId && ce.course === selectedCourse.id
          );

          if (existingEnrollment) {
            enrollmentUpdates.push({
              enrollmentId: existingEnrollment.id,
              enrollment: studentId,
              course: selectedCourse.id,
              start_date: startDate,
              end_date: endDateRaw,
              status: 'enrolled',
              coursegroup: null
            });
            enrollmentPromises.push(
              axios.put(
                `/api/course-enrollments/${existingEnrollment.id}`,
                {
                  start_date: startDate,
                  end_date: endDateRaw,
                  status: 'enrolled'
                },
                { headers: { Authorization: `Bearer ${token}` } }
              )
            );
          } else {
            newEnrollmentsToAdd.push({
              enrollment: studentId,
              course: selectedCourse.id,
              start_date: startDate,
              end_date: endDateRaw,
              status: 'enrolled',
              createdby: userData?.account_id || null,
              coursegroup: null
            });
            enrollmentPromises.push(
              axios.post(
                '/api/course-enrollments',
                {
                  enrollment: studentId,
                  course: selectedCourse.id,
                  start_date: startDate,
                  end_date: endDateRaw,
                  status: 'enrolled',
                  createdby: userData?.account_id || null
                },
                { headers: { Authorization: `Bearer ${token}` } }
              )
            );
          }
        }

        await Promise.all(enrollmentPromises);

        setCourseEnrollments((prev) => {
          const updated = [...prev];
          enrollmentUpdates.forEach((update) => {
            const index = updated.findIndex((ce) => ce.id === update.enrollmentId);
            if (index !== -1) {
              updated[index] = {
                ...updated[index],
                status: 'enrolled',
                start_date: update.start_date,
                end_date: update.end_date,
                coursegroup: update.coursegroup
              };
            }
          });

          const selectedCourseEnrollments = newEnrollmentsToAdd.map((ne) => {
            const student = allStudents.find((s) => s.enrollment_id === ne.enrollment);
            return {
              id: `temp-${ne.enrollment}-${ne.course}`,
              course: ne.course,
              enrollment: ne.enrollment,
              first_name: student?.first_name || '',
              last_name: student?.last_name || '',
              primary_email: student?.email || '',
              start_date: ne.start_date,
              end_date: ne.end_date,
              status: 'enrolled',
              coursegroup: ne.coursegroup
            };
          });

          return [...updated, ...selectedCourseEnrollments];
        });

        const studentsToAddToList = allStudents
          .filter((student) => {
            if (!selectedStudentsToAdd.includes(student.enrollment_id)) return false;
            const hasSelectedCourseEnrollment =
              newEnrollmentsToAdd.some(
                (ne) => ne.enrollment === student.enrollment_id && ne.course === selectedCourse.id
              ) ||
              enrollmentUpdates.some(
                (eu) => eu.enrollment === student.enrollment_id && eu.course === selectedCourse.id
              );
            return hasSelectedCourseEnrollment;
          })
          .map((student) => ({
            id: `temp-${student.enrollment_id}`,
            enrollment_id: student.enrollment_id,
            first_name: student.first_name,
            last_name: student.last_name,
            primary_email: student.email,
            start_date: startDate,
            createddate: new Date().toISOString(),
            progress: {
              completed: 0,
              total: courseComponents[selectedCourse.id]?.length || 0,
              percentage: 0,
              pending: 0,
              lastSubmission: null
            }
          }));

        setStudents((prevStudents) => {
          const existingIds = new Set(prevStudents.map((s) => s.enrollment_id));
          const toAdd = studentsToAddToList.filter((s) => !existingIds.has(s.enrollment_id));
          return [...prevStudents, ...toAdd];
        });

        setSelectedStudentsToAdd([]);
        setStudentSearchTerm('');
        setSelectedCourseGroup('');
        setAddStudentStartDate('');
        setAddStudentEndDate('');
        setShowAddStudentModal(false);

        alert(
          `Successfully enrolled ${selectedStudentsToAdd.length} student(s) in "${selectedCourse.title}"`
        );
        return;
      }

      // Course group: enroll in all courses in the group
      const selectedGroup = courseGroups.find((group) => group.id === selectedCourseGroup);
      if (!selectedGroup || !selectedGroup.courses || !Array.isArray(selectedGroup.courses)) {
        alert('Course group not found or has no courses');
        return;
      }

      const coursesInGroup = selectedGroup.courses;
      
      // Enroll each selected student in ALL courses of the course group
      const enrollmentPromises = [];
      const enrollmentUpdates = [];
      const newEnrollmentsToAdd = [];

      for (const studentId of selectedStudentsToAdd) {
        for (const courseId of coursesInGroup) {
          // Get start_date from setup for this specific course
          let enrollmentStartDate = new Date().toISOString().split('T')[0]; // Default to current date
          if (selectedGroup.setup && Array.isArray(selectedGroup.setup)) {
            const courseSetup = selectedGroup.setup.find(
              setup => setup.course_id === courseId
            );
            if (courseSetup && courseSetup.start_date) {
              enrollmentStartDate = courseSetup.start_date;
            }
          }

          // Check if enrollment already exists (even if withdrawn)
          const existingEnrollment = courseEnrollments.find(
            ce => ce.enrollment === studentId && ce.course === courseId
          );

          if (existingEnrollment) {
            // Update existing enrollment to 'enrolled' status
            enrollmentUpdates.push({
              enrollmentId: existingEnrollment.id,
              enrollment: studentId,
              course: courseId,
              start_date: enrollmentStartDate,
              status: 'enrolled',
              coursegroup: selectedCourseGroup
            });
            // Use PUT /:id endpoint to update both status and start_date
            enrollmentPromises.push(
              axios.put(`/api/course-enrollments/${existingEnrollment.id}`, {
                start_date: enrollmentStartDate,
                status: 'enrolled'
              }, {
                headers: { Authorization: `Bearer ${token}` }
              })
            );
          } else {
            // Create new enrollment
            newEnrollmentsToAdd.push({
              enrollment: studentId,
              course: courseId,
              start_date: enrollmentStartDate,
              status: 'enrolled',
              createdby: userData?.account_id || null,
              coursegroup: selectedCourseGroup
            });
            enrollmentPromises.push(
              axios.post('/api/course-enrollments', {
                enrollment: studentId,
                course: courseId,
                start_date: enrollmentStartDate,
                status: 'enrolled',
                createdby: userData?.account_id || null,
                coursegroup: selectedCourseGroup
              }, {
                headers: { Authorization: `Bearer ${token}` }
              })
            );
          }
        }
      }

      await Promise.all(enrollmentPromises);

      // Update courseEnrollments state - update existing ones and add new ones
      setCourseEnrollments(prev => {
        const updated = [...prev];
        
        // Update existing enrollments that were withdrawn/not enrolled
        enrollmentUpdates.forEach(update => {
          const index = updated.findIndex(ce => ce.id === update.enrollmentId);
          if (index !== -1) {
            updated[index] = {
              ...updated[index],
              status: 'enrolled',
              start_date: update.start_date,
              coursegroup: update.coursegroup
            };
          }
        });

        // Add new enrollments for the selected course (for UI display)
        const selectedCourseEnrollments = newEnrollmentsToAdd
          .filter(ne => ne.course === selectedCourse.id)
          .map(ne => {
            const student = allStudents.find(s => s.enrollment_id === ne.enrollment);
            return {
              id: `temp-${ne.enrollment}-${ne.course}`,
              course: ne.course,
              enrollment: ne.enrollment,
              first_name: student?.first_name || '',
              last_name: student?.last_name || '',
              primary_email: student?.email || '',
              start_date: ne.start_date,
              status: 'enrolled',
              coursegroup: ne.coursegroup
            };
          });
        
        return [...updated, ...selectedCourseEnrollments];
      });

      // Add newly enrolled students to the current students list (only for the selected course)
      // This includes both newly enrolled students and students who were re-enrolled (had withdrawn status)
      const studentsToAddToList = allStudents
        .filter(student => {
          // Include if they're in the selected students list
          if (!selectedStudentsToAdd.includes(student.enrollment_id)) return false;
          
          // Check if they have an enrollment for the selected course (either new or updated)
          const hasSelectedCourseEnrollment = 
            newEnrollmentsToAdd.some(ne => ne.enrollment === student.enrollment_id && ne.course === selectedCourse.id) ||
            enrollmentUpdates.some(eu => eu.enrollment === student.enrollment_id && eu.course === selectedCourse.id);
          
          return hasSelectedCourseEnrollment;
        })
        .map(student => {
          // Get start_date for the selected course
          let enrollmentStartDate = new Date().toISOString().split('T')[0];
          if (selectedGroup.setup && Array.isArray(selectedGroup.setup)) {
            const courseSetup = selectedGroup.setup.find(
              setup => setup.course_id === selectedCourse.id
            );
            if (courseSetup && courseSetup.start_date) {
              enrollmentStartDate = courseSetup.start_date;
            }
          }

          return {
            id: `temp-${student.enrollment_id}`,
            enrollment_id: student.enrollment_id,
            first_name: student.first_name,
            last_name: student.last_name,
            primary_email: student.email,
            start_date: enrollmentStartDate,
            createddate: new Date().toISOString(),
            progress: {
              completed: 0,
              total: courseComponents[selectedCourse.id]?.length || 0,
              percentage: 0,
              pending: 0,
              lastSubmission: null
            }
          };
        });

      // Only add students that aren't already in the list (in case they were withdrawn and now re-enrolled)
      setStudents(prevStudents => {
        const existingIds = new Set(prevStudents.map(s => s.enrollment_id));
        const toAdd = studentsToAddToList.filter(s => !existingIds.has(s.enrollment_id));
        return [...prevStudents, ...toAdd];
      });

      // Reset form and close modal
      setSelectedStudentsToAdd([]);
      setStudentSearchTerm('');
      setSelectedCourseGroup('');
      setAddStudentStartDate('');
      setAddStudentEndDate('');
      setShowAddStudentModal(false);

      const totalCourses = coursesInGroup.length;
      alert(`Successfully enrolled ${selectedStudentsToAdd.length} student(s) in ${totalCourses} course(s) of the course group`);
    } catch (error) {
      console.error('Error enrolling students:', error);
      alert('Failed to enroll students. Please try again.');
    }
  };

  // Toggle student selection for adding to course
  const toggleStudentSelection = (studentId) => {
    if (selectedStudentsToAdd.includes(studentId)) {
      setSelectedStudentsToAdd(selectedStudentsToAdd.filter(id => id !== studentId));
    } else {
      setSelectedStudentsToAdd([...selectedStudentsToAdd, studentId]);
    }
  };

  // Handle opening copy roster modal
  const handleOpenCopyRoster = () => {
    if (!selectedCourse) return;
    
    // Get all enrollments for the current course
    const courseEnrollmentsForCourse = courseEnrollments.filter(
      enrollment => enrollment.course === selectedCourse.id
    );
    
    // Initialize all students as selected (checked)
    setSelectedStudentsToCopy(courseEnrollmentsForCourse.map(enrollment => enrollment.id));
    setTargetCourseId('');
    setCopyStartDate(getCurrentDate());
    setCopyEndDate('');
    setSetPreviousCourse(true); // Default checked
    setShowCopyRosterModal(true);
  };

  // Toggle student selection for copying roster
  const toggleStudentCopySelection = (enrollmentId) => {
    if (selectedStudentsToCopy.includes(enrollmentId)) {
      setSelectedStudentsToCopy(selectedStudentsToCopy.filter(id => id !== enrollmentId));
    } else {
      setSelectedStudentsToCopy([...selectedStudentsToCopy, enrollmentId]);
    }
  };

  // Handle copying roster to another course
  const handleCopyRoster = async () => {
    if (!targetCourseId || selectedStudentsToCopy.length === 0 || !selectedCourse) {
      alert('Please select a target course and at least one student to copy');
      return;
    }

    const targetCourse = courses.find(c => c.id === targetCourseId);
    if (targetCourse && targetCourse.modlock !== true) {
      alert('Enrollment is not allowed for the target course because it is not locked for modifications.');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      
      // Get the selected enrollments
      const enrollmentsToCopy = courseEnrollments.filter(
        enrollment => selectedStudentsToCopy.includes(enrollment.id)
      );

      // Create enrollments for each selected student in the target course
      const enrollmentPromises = enrollmentsToCopy.map(enrollment =>
        axios.post('/api/course-enrollments', {
          enrollment: enrollment.enrollment,
          course: targetCourseId,
          start_date: copyStartDate || null,
          end_date: copyEndDate || null,
          status: 'enrolled',
          createdby: userData?.account_id || null,
          previous_course: setPreviousCourse ? selectedCourse.id : null
        }, {
          headers: { Authorization: `Bearer ${token}` }
        }).catch(error => {
          // If student is already enrolled, that's okay - just log it
          if (error.response?.status === 400 && error.response?.data?.error?.includes('already enrolled')) {
            console.log(`Student ${enrollment.enrollment} is already enrolled in target course`);
            return { skipped: true };
          }
          throw error;
        })
      );

      const results = await Promise.all(enrollmentPromises);
      const successful = results.filter(r => r && !r.skipped).length;
      const skipped = results.filter(r => r && r.skipped).length;

      // Reset form and close modal
      setSelectedStudentsToCopy([]);
      setTargetCourseId('');
      setCopyStartDate(getCurrentDate());
      setCopyEndDate('');
      setSetPreviousCourse(true);
      setShowCopyRosterModal(false);

      // Reload data to show updated enrollments
      await loadData();

      let message = `Successfully copied ${successful} student(s) to the target course`;
      if (skipped > 0) {
        message += ` (${skipped} were already enrolled and skipped)`;
      }
      alert(message);
    } catch (error) {
      console.error('Error copying roster:', error);
      alert('Failed to copy roster. Please try again.');
    }
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <LoadingSpinner />
      </div>
    );
  }

  if (error) {
    if (criticalError) {
      return (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.8)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999
        }}>
          <div style={{
            backgroundColor: '#fff',
            padding: '2rem',
            borderRadius: '12px',
            maxWidth: '500px',
            width: '90%',
            textAlign: 'center',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
          }}>
            <div style={{
              fontSize: '48px',
              marginBottom: '1rem',
              color: '#ef4444'
            }}>
              ⚠️
            </div>
            <h2 style={{
              color: '#ef4444',
              marginBottom: '1rem',
              fontSize: '1.5rem',
              fontWeight: '600'
            }}>
              Critical Error
            </h2>
            <p style={{
              color: '#374151',
              marginBottom: '1.5rem',
              lineHeight: '1.5'
            }}>
              {error}
            </p>
            <p style={{
              color: '#6b7280',
              marginBottom: '2rem',
              fontSize: '0.9rem'
            }}>
              Some critical data failed to load. Please reload the page to try again.
            </p>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
              <button 
                onClick={() => window.location.reload()}
                style={{
                  padding: '12px 24px',
                  backgroundColor: '#ef4444',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '16px',
                  fontWeight: '600',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = '#dc2626';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = '#ef4444';
                }}
              >
                Reload Page
              </button>
              <button 
                onClick={loadData}
                style={{
                  padding: '12px 24px',
                  backgroundColor: '#6b7280',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '16px',
                  fontWeight: '600',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = '#4b5563';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = '#6b7280';
                }}
              >
                Try Again
              </button>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div style={{ 
        padding: '20px', 
        textAlign: 'center', 
        color: '#ef4444',
        backgroundColor: '#fef2f2',
        border: '1px solid #fecaca',
        borderRadius: '8px',
        margin: '20px'
      }}>
        <h3>Error</h3>
        <p>{error}</p>
        <button 
          onClick={loadData}
          style={{
            padding: '8px 16px',
            backgroundColor: '#3b82f6',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            marginTop: '10px'
          }}
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="my-courses-manager">
      <div className="header">
        <div>
          <h1>{isFullAccess ? 'LMS Roster & Progress' : 'My Courses'}</h1>
          <p>{isFullAccess ? 'Manage all courses and view student enrollments' : 'Manage your courses and view student enrollments'}</p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          {!isFullAccess && (
            <button
              onClick={() => setShowAllCourses(!showAllCourses)}
              style={{
                background: showAllCourses ? '#2563eb' : '#f8fafc',
                color: showAllCourses ? 'white' : '#374151',
                border: '1px solid #d1d5db',
                padding: '8px 16px',
                borderRadius: '6px',
                fontSize: '14px',
                fontWeight: '500',
                cursor: 'pointer',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                if (!showAllCourses) {
                  e.target.style.backgroundColor = '#e5e7eb';
                }
              }}
              onMouseLeave={(e) => {
                if (!showAllCourses) {
                  e.target.style.backgroundColor = '#f8fafc';
                }
              }}
            >
              {showAllCourses ? 'Show My Courses' : 'Load All Courses'}
            </button>
          )}
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <label style={{ fontSize: '14px', fontWeight: '500', color: '#374151' }}>Sort by:</label>
            <select 
              value={courseSortBy}
              onChange={(e) => setCourseSortBy(e.target.value)}
              style={{
                background: '#f8fafc',
                color: '#374151',
                border: '1px solid #d1d5db',
                padding: '6px 12px',
                borderRadius: '6px',
                fontSize: '14px',
                fontWeight: '500',
                cursor: 'pointer',
                minWidth: '140px'
              }}
            >
              <option value="title">Title (A-Z)</option>
              <option value="students">Number of Students</option>
              <option value="pending">Items to Review</option>
              <option value="flags">Flags</option>
              <option value="offeredhours">Offered Hours</option>
              <option value="createddate">Created Date</option>
              <option value="creator">Creator</option>
            </select>
            <button 
              onClick={() => setCourseSortOrder(courseSortOrder === 'asc' ? 'desc' : 'asc')}
              style={{
                background: '#f8fafc',
                color: '#374151',
                border: '1px solid #d1d5db',
                padding: '6px 10px',
                borderRadius: '6px',
                fontSize: '16px',
                fontWeight: '600',
                cursor: 'pointer',
                minWidth: '36px',
                height: '36px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
              title={`Sort ${courseSortOrder === 'asc' ? 'Descending' : 'Ascending'}`}
            >
              {courseSortOrder === 'asc' ? '↑' : '↓'}
            </button>
          </div>
        </div>
      </div>

      {flaggedCount > 0 && (
        <div className="flagged-courses-banner" style={{
          padding: '6px 12px',
          marginBottom: '12px',
          backgroundColor: '#fef2f2',
          border: '1px solid #fecaca',
          borderRadius: '4px',
          color: '#991b1b',
          fontSize: '12px',
          fontWeight: '500'
        }}>
          {flaggedCount} course{flaggedCount !== 1 ? 's' : ''} {flaggedCount !== 1 ? 'have' : 'has'} been flagged and {flaggedCount !== 1 ? 'are' : 'is'} not ready for students to be enrolled.
        </div>
      )}

      {coursesWithItemsToReviewCount > 0 && (
        <div className="items-to-review-banner" style={{
          padding: '6px 12px',
          marginBottom: '12px',
          backgroundColor: '#fffbeb',
          border: '1px solid #fde68a',
          borderRadius: '4px',
          color: '#92400e',
          fontSize: '12px',
          fontWeight: '500'
        }}>
          {coursesWithItemsToReviewCount} course{coursesWithItemsToReviewCount !== 1 ? 's' : ''} {coursesWithItemsToReviewCount !== 1 ? 'have' : 'has'} items to review.
        </div>
      )}

      <div className="courses-list">
        <div className="courses-list-top-right-filters" role="radiogroup" aria-label="Course type filter">
          <button
            type="button"
            className={`course-type-filter-tag ${courseTypeFilter === 'all' ? 'active' : ''}`}
            onClick={() => setCourseTypeFilter('all')}
            aria-pressed={courseTypeFilter === 'all'}
          >
            All
          </button>
          <button
            type="button"
            className={`course-type-filter-tag ${courseTypeFilter === 'cte' ? 'active' : ''}`}
            onClick={() => setCourseTypeFilter('cte')}
            aria-pressed={courseTypeFilter === 'cte'}
          >
            CTE
          </button>
          <button
            type="button"
            className={`course-type-filter-tag ${courseTypeFilter === 'general' ? 'active' : ''}`}
            onClick={() => setCourseTypeFilter('general')}
            aria-pressed={courseTypeFilter === 'general'}
          >
            General
          </button>
        </div>
        {filteredCourseList.length === 0 ? (
          <div className="no-courses">
            <h3>No courses found</h3>
            <p>
              {courses.length === 0
                ? (isFullAccess ? 'No courses are available in the system.' : "You haven't created any courses yet.")
                : 'No courses match the current type filter.'}
            </p>
          </div>
        ) : (
          <>
            {(() => {
              const colDefs = [
                { key: 'title', label: 'Course', show: true, width: '1fr' },
                { key: 'hours', label: 'Hours', show: showColumns.hours, width: 'minmax(140px, auto)' },
                { key: 'students', label: 'Students', show: showColumns.students, width: '56px' },
                { key: 'review', label: 'Review', show: showColumns.review, width: '56px' },
                { key: 'flags', label: 'Flags', show: showColumns.flags, width: '48px' },
                { key: 'created', label: 'Created', show: showColumns.created, width: '90px' },
                { key: 'creator', label: 'Creator', show: showColumns.creator, width: 'minmax(100px, 1fr)' }
              ];
              const visibleCols = colDefs.filter(c => c.show);
              const gridTemplate = visibleCols.map(c => c.width).join(' ');
              return (
                <>
                  <div className="courses-list-header" style={{ gridTemplateColumns: gridTemplate }}>
                    {visibleCols.map(c => (
                      <span key={c.key} className={`list-col-${c.key}`}>{c.label}</span>
                    ))}
                  </div>
                  {filteredCourseList.map(course => (
                    <div
                      key={course.id}
                      className="course-list-row"
                      style={{ gridTemplateColumns: gridTemplate }}
                      onClick={() => handleCourseClick(course)}
                    >
                      <span className="list-col-title" title={course.title || 'Untitled Course'}>
                        <span className="list-col-title-inner">
                          <CourseBuilderOpenIconLink courseId={course.id} canOpen={canOpenCourseBuilder} />
                          <CourseDeleteIconButton
                            courseId={course.id}
                            canDelete={canDeleteCourse}
                            isDeleting={deletingCourseIds.has(course.id)}
                            onDelete={handleDeleteCourse}
                          />
                          <span className="list-col-title-text">{course.title || 'Untitled Course'}</span>
                        </span>
                      </span>
                      {showColumns.hours && (
                        <div className="list-col-hours">
                          {(() => {
                            const hb = courseMetricsByCourseId[course.id]?.hoursBreakdown ?? getCourseHoursBreakdown(course.id);
                            return (
                              <>
                                <span className="course-hours-chip course-hours-total">Total - {hb.total}</span>
                                {hb.types.filter(t => hb.breakdown[t] > 0).map((t) => (
                                  <span key={t} className="course-hours-chip">{t} - {hb.breakdown[t]}</span>
                                ))}
                              </>
                            );
                          })()}
                        </div>
                      )}
                      {showColumns.students && (
                        <span className={`list-col-students ${(courseMetricsByCourseId[course.id]?.enrolledCount ?? 0) === 0 ? 'muted' : ''}`}>
                          {courseMetricsByCourseId[course.id]?.enrolledCount ?? 0}
                        </span>
                      )}
                      {showColumns.review && (
                        <span className={`list-col-review ${(courseMetricsByCourseId[course.id]?.pendingSum ?? 0) === 0 ? 'muted' : 'highlight'}`}>
                          {courseMetricsByCourseId[course.id]?.pendingSum ?? 0}
                        </span>
                      )}
                      {showColumns.flags && (
                        <span className={`list-col-flags ${(courseMetricsByCourseId[course.id]?.flagsCount ?? 0) === 0 ? 'muted' : 'highlight'}`}>
                          {courseMetricsByCourseId[course.id]?.flagsCount ?? 0}
                        </span>
                      )}
                      {showColumns.created && (
                        <span className="list-col-created">{formatDate(course.createddate)}</span>
                      )}
                      {showColumns.creator && (
                        <span className="list-col-creator" title={formatCourseCreatorLabel(course, userData)}>
                          {formatCourseCreatorLabel(course, userData)}
                        </span>
                      )}
                    </div>
                  ))}
                </>
              );
            })()}
          </>
        )}
      </div>

      {filteredCourseList.length > 0 && (
        <div className="courses-list-column-toggles">
          <span className="column-toggles-label">Show columns:</span>
          {[
            { key: 'hours', label: 'Hours' },
            { key: 'students', label: 'Students' },
            { key: 'review', label: 'Review' },
            { key: 'flags', label: 'Flags' },
            { key: 'created', label: 'Created' },
            { key: 'creator', label: 'Creator' }
          ].map(({ key, label }) => (
            <label key={key} className="column-toggle-checkbox">
              <input
                type="checkbox"
                checked={showColumns[key]}
                onChange={(e) => setShowColumns(prev => ({ ...prev, [key]: e.target.checked }))}
              />
              <span>{label}</span>
            </label>
          ))}
        </div>
      )}

      {/* Students Modal */}
      {showStudentsModal && (
        <div className="modal-overlay" onClick={() => setShowStudentsModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header" style={{ alignItems: 'flex-start' }}>
              <div style={{ flex: 1, minWidth: 0, paddingRight: 16 }}>
                <h2>Students in "{selectedCourse?.title}"</h2>
                {selectedCourseOfferedHours && (
                  <div
                    style={{
                      marginTop: 10,
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: 6,
                      alignItems: 'center'
                    }}
                  >
                    <span style={{ fontSize: 12, color: '#64748b', fontWeight: 600 }}>
                      Hours offered:
                    </span>
                    <span className="course-hours-chip course-hours-total">
                      Total — {selectedCourseOfferedHours.total}h
                    </span>
                    {selectedCourseOfferedHours.types
                      .filter((t) => selectedCourseOfferedHours.breakdown[t] > 0)
                      .map((t) => (
                        <span key={t} className="course-hours-chip">
                          {t} — {selectedCourseOfferedHours.breakdown[t]}h
                        </span>
                      ))}
                  </div>
                )}
              </div>
              <div className="modal-header-actions" style={{ flexShrink: 0, paddingTop: 2 }}>
                <div className="sort-controls" style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '8px 12px',
                  backgroundColor: '#f8fafc',
                  border: '1px solid #e2e8f0',
                  borderRadius: '8px'
                }}>
                  <select 
                    className="sort-select"
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    style={{
                      background: '#ffffff',
                      color: '#374151',
                      border: '1px solid #d1d5db',
                      padding: '6px 8px',
                      borderRadius: '4px',
                      fontSize: '14px',
                      fontWeight: '500',
                      cursor: 'pointer',
                      minWidth: '120px'
                    }}
                  >
                    <option value="progress">Progress</option>
                    <option value="hoursEarned">Hours Earned</option>
                    <option value="name">Name</option>
                      <option value="lastSubmission">Last Submission</option>
                      <option value="pending">Pending Items</option>
                      <option value="startDate">Cohort Date</option>
                      <option value="createddate">Date-Time Added</option>
                    </select>
                  <button 
                    className="sort-order-button"
                    onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
                    title={`Sort ${sortOrder === 'asc' ? 'Descending' : 'Ascending'}`}
                    style={{
                      background: '#ffffff',
                      color: '#374151',
                      border: '1px solid #d1d5db',
                      padding: '6px 10px',
                      borderRadius: '4px',
                      fontSize: '16px',
                      fontWeight: '600',
                      cursor: 'pointer',
                      minWidth: '36px',
                      height: '36px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    {sortOrder === 'asc' ? '↑' : '↓'}
                  </button>
                </div>
                <button 
                  className="close-button"
                  onClick={() => setShowStudentsModal(false)}
                >
                  ×
                </button>
              </div>
            </div>
            
            <div className="modal-body">
              <div style={{ 
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '8px',
                flexWrap: 'wrap',
                gap: '8px'
              }}>
                <div style={{ 
                  fontSize: '12px', 
                  color: '#6b7280', 
                  fontWeight: '500'
                }}>
                  {(() => {
                    const filteredStudents = filterStudentsByDateRange(students, startDateFrom, startDateTo);
                    const totalCount = students.length;
                    const filteredCount = filteredStudents.length;
                    if (totalCount === 0) {
                      return 'No students enrolled';
                    }
                    if (startDateFrom || startDateTo) {
                      return `Total Students: ${totalCount} | Showing: ${filteredCount}`;
                    }
                    return `Total Students: ${totalCount}`;
                  })()}
                </div>
                <div style={{ 
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  <div style={{ 
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    fontSize: '11px'
                  }}>
                    <label style={{ 
                      color: '#6b7280', 
                      fontWeight: '500',
                      whiteSpace: 'nowrap'
                    }}>
                      Cohort From:
                    </label>
                    <input
                      type="date"
                      value={startDateFrom}
                      onChange={(e) => setStartDateFrom(e.target.value)}
                      style={{
                        padding: '4px 6px',
                        borderRadius: '4px',
                        border: '1px solid #d1d5db',
                        fontSize: '11px',
                        width: '120px'
                      }}
                    />
                    <label style={{ 
                      color: '#6b7280', 
                      fontWeight: '500',
                      whiteSpace: 'nowrap'
                    }}>
                      To:
                    </label>
                    <input
                      type="date"
                      value={startDateTo}
                      onChange={(e) => setStartDateTo(e.target.value)}
                      style={{
                        padding: '4px 6px',
                        borderRadius: '4px',
                        border: '1px solid #d1d5db',
                        fontSize: '11px',
                        width: '120px'
                      }}
                    />
                  </div>
                  <button 
                    className="csv-download-button"
                    onClick={downloadCSV}
                    disabled={students.length === 0}
                    title="Download student progress as CSV"
                    style={{
                      background: '#ffffff',
                      color: '#374151',
                      border: '1px solid #d1d5db',
                      padding: '8px 12px',
                      borderRadius: '6px',
                      fontSize: '16px',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      minWidth: '40px',
                      height: '40px',
                      opacity: students.length === 0 ? 0.5 : 1
                    }}
                  >
                    📥
                  </button>
                  <button 
                    className="copy-emails-button"
                    onClick={copyEmailsToClipboard}
                    disabled={students.length === 0}
                    title="Copy student emails to clipboard"
                    style={{
                      background: '#ffffff',
                      color: '#374151',
                      border: '1px solid #d1d5db',
                      padding: '8px 12px',
                      borderRadius: '6px',
                      fontSize: '14px',
                      fontWeight: '500',
                      cursor: students.length === 0 ? 'not-allowed' : 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '6px',
                      height: '40px',
                      opacity: students.length === 0 ? 0.5 : 1
                    }}
                  >
                    <span>📨📋</span>
                  </button>
                  <button
                    onClick={() => {
                      if (selectedCourse?.modlock !== true) return;
                      setAddStudentStartDate('');
                      setAddStudentEndDate('');
                      setShowAddStudentModal(true);
                    }}
                    disabled={selectedCourse?.modlock !== true}
                    title={selectedCourse?.modlock !== true ? 'Enrollment is not allowed for this course because it is not locked for modifications' : 'Add students to this course'}
                    style={{
                      background: selectedCourse?.modlock === true ? '#ffffff' : '#f3f4f6',
                      color: selectedCourse?.modlock === true ? '#374151' : '#9ca3af',
                      border: '1px solid #d1d5db',
                      padding: '8px 12px',
                      borderRadius: '6px',
                      fontSize: '14px',
                      fontWeight: '500',
                      cursor: selectedCourse?.modlock === true ? 'pointer' : 'not-allowed',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      height: '40px',
                      transition: 'all 0.2s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.backgroundColor = '#f3f4f6';
                      e.target.style.borderColor = '#9ca3af';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.backgroundColor = '#ffffff';
                      e.target.style.borderColor = '#d1d5db';
                    }}
                  >
                    ➕
                  </button>
                  {copyRosterAllowed && (
                    <button 
                      onClick={handleOpenCopyRoster}
                      style={{
                        background: '#28a745',
                        color: 'white',
                        border: '1px solid #28a745',
                        padding: '4px 8px',
                        borderRadius: '4px',
                        fontSize: '11px',
                        fontWeight: '500',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease'
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.backgroundColor = '#218838';
                        e.target.style.borderColor = '#218838';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.backgroundColor = '#28a745';
                        e.target.style.borderColor = '#28a745';
                      }}
                    >
                      Copy Roster
                    </button>
                  )}
                </div>
              </div>
              {(() => {
                const filteredStudents = filterStudentsByDateRange(students, startDateFrom, startDateTo);
                if (students.length === 0) {
                  return (
                    <div className="no-students">
                      <p>No students are enrolled in this course.</p>
                    </div>
                  );
                }
                if (filteredStudents.length === 0) {
                  return (
                    <div className="no-students">
                      <p>No students match the selected date range filter.</p>
                    </div>
                  );
                }
                const showWheelColumn = selectedCourse?.course_track !== 'TK12';
                const rosterColCount =
                  7 +
                  (showWheelColumn ? 1 : 0) +
                  ((deletionsAllowed || withdrawalsAllowed) ? 1 : 0);
                return (
                  <div className="students-table">
                  <table>
                    <thead>
                      <tr>
                        {(deletionsAllowed || withdrawalsAllowed) && <th style={{ width: '32px' }}></th>}
                        <th style={{ width: '24px', minWidth: '24px', maxWidth: '24px', padding: '8px 4px' }}>#</th>
                        <th>Name</th>
                        <th>Last Submission</th>
                        <th>Progress</th>
                        <th>
                          Pending (<span style={{ color: '#e67e22' }}>{(() => {
                            const filteredStudents = filterStudentsByDateRange(students, startDateFrom, startDateTo);
                            return filteredStudents.reduce((sum, student) => sum + student.progress.pending, 0);
                          })()}</span>)
                        </th>
                        <th>Cohort Date</th>
                        <th>Added at</th>
                        {showWheelColumn && <th>Wheel</th>}
                      </tr>
                    </thead>
                    <tbody>
                      {(() => {
                        const filteredStudents = filterStudentsByDateRange(students, startDateFrom, startDateTo);
                        return sortStudents(filteredStudents, sortBy, sortOrder).map((student, index) => {
                          const studentSubmissions = getStudentComponentSubmissions(student.id);
                          const isExpanded = expandedStudent === student.id;
                          
                          return (
                            <React.Fragment key={student.id}>
                            <tr 
                              className={`student-row ${isExpanded ? 'expanded' : ''}`}
                              onClick={() => handleStudentRowClick(student.id)}
                              style={{ cursor: 'pointer' }}
                            >
                              {(deletionsAllowed || withdrawalsAllowed) && (
                                <td style={{ textAlign: 'right' }}>
                                  {withdrawalsAllowed && (
                                    <button
                                      onClick={(e) => handleOpenWithdrawalModal(student, e)}
                                      title="Withdraw from course"
                                      style={{
                                        background: 'transparent',
                                        border: 'none',
                                        color: '#eab308',
                                        cursor: 'pointer',
                                        fontSize: '16px',
                                        lineHeight: 1,
                                        padding: 0
                                      }}
                                      onMouseEnter={(e) => { e.target.style.color = '#ca8a04'; }}
                                      onMouseLeave={(e) => { e.target.style.color = '#eab308'; }}
                                    >
                                      ×
                                    </button>
                                  )}
                                  {deletionsAllowed && (
                                    <button
                                      onClick={(e) => handleRemoveEnrollment(student, e)}
                                      title="Remove from course"
                                      style={{
                                        background: 'transparent',
                                        border: 'none',
                                        color: '#dc2626',
                                        cursor: 'pointer',
                                        fontSize: '16px',
                                        lineHeight: 1,
                                        padding: 0
                                      }}
                                      onMouseEnter={(e) => { e.target.style.color = '#b91c1c'; }}
                                      onMouseLeave={(e) => { e.target.style.color = '#dc2626'; }}
                                    >
                                      ×
                                    </button>
                                  )}
                                </td>
                              )}
                              <td style={{ 
                                textAlign: 'left', 
                                fontWeight: '500', 
                                color: '#6b7280',
                                width: '24px',
                                minWidth: '24px',
                                maxWidth: '24px',
                                padding: '8px 4px',
                                verticalAlign: 'top'
                              }}>
                                <div style={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  width: '20px',
                                  height: '20px',
                                  backgroundColor: '#f3f4f6',
                                  borderRadius: '4px',
                                  fontSize: '11px',
                                  fontWeight: '600',
                                  color: '#6b7280'
                                }}>
                                  {index + 1}
                                </div>
                              </td>
                              <td>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                  <span style={{ fontSize: '12px', color: '#666' }}>
                                    {isExpanded ? '▼' : '▶'}
                                  </span>
                                  {student.first_name} {student.last_name}
                                </div>
                              </td>
                              <td>
                                {student.progress.lastSubmission ? (
                                  <span className="last-submission-date">
                                    {formatDate(student.progress.lastSubmission)}
                                  </span>
                                ) : (
                                  <span className="no-submissions">No submissions</span>
                                )}
                              </td>
                              <td>
                                <div className="progress-container" style={{ flexDirection: 'column', alignItems: 'flex-start', gap: '2px' }}>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '4px', width: '100%' }}>
                                    <div className="progress-bar">
                                      <div 
                                        className="progress-fill" 
                                        style={{ width: `${student.progress.percentage}%` }}
                                      ></div>
                                    </div>
                                    <span className="progress-text">
                                      {student.progress.completed}/{student.progress.total} ({student.progress.percentage}%)
                                    </span>
                                  </div>
                                  <span
                                    style={{
                                      fontSize: '11px',
                                      color: '#6b7280',
                                      fontWeight: 500,
                                      cursor: student.progress.hoursEarnedTooltip ? 'help' : 'default',
                                      borderBottom: student.progress.hoursEarnedTooltip ? '1px dotted #cbd5e1' : 'none'
                                    }}
                                    title={student.progress.hoursEarnedTooltip || undefined}
                                  >
                                    {student.progress.totalHours > 0 ? `${student.progress.totalHours}h` : '—'}
                                  </span>
                                </div>
                              </td>
                              <td>
                                <div className="pending-container">
                                  {student.progress.pending > 0 ? (
                                    <span className="pending-badge">
                                      {student.progress.pending} item{student.progress.pending !== 1 ? 's' : ''}
                                    </span>
                                  ) : (
                                    <span className="no-pending">None</span>
                                  )}
                                </div>
                              </td>
                              <td>
                                {student.start_date ? (
                                  <span className="start-date">
                                    {formatDateOnly(student.start_date)}
                                  </span>
                                ) : (
                                  <span className="no-start-date">N/A</span>
                                )}
                              </td>
                              <td>
                                {student.createddate ? (
                                  <span className="last-submission-date">
                                    {formatDate(student.createddate)}
                                  </span>
                                ) : (
                                  <span className="no-submissions">N/A</span>
                                )}
                              </td>
                              {showWheelColumn && (
                                <td>
                                  {student.coursegroup_title ? (
                                    <span
                                      title={student.coursegroup_title}
                                      style={{
                                        display: 'inline-block',
                                        padding: '4px 8px',
                                        borderRadius: '4px',
                                        fontSize: '12px',
                                        fontWeight: '500',
                                        backgroundColor: getCourseGroupColor(student.coursegroup_title),
                                        color: '#fff',
                                        cursor: 'help'
                                      }}
                                    >
                                      {student.coursegroup_title.substring(0, 2).toUpperCase()}
                                    </span>
                                  ) : (
                                    <span className="no-submissions">N/A</span>
                                  )}
                                </td>
                              )}
                            </tr>
                            
                            {/* Expanded submissions row */}
                            {isExpanded && (
                              <tr className="submissions-row">
                                <td colSpan={rosterColCount} style={{ padding: '0', backgroundColor: '#f8f9fa' }}>
                                  <div style={{ padding: '16px' }}>
                                    <h4 style={{ margin: '0 0 12px 0', fontSize: '14px', fontWeight: '600', color: '#495057' }}>
                                      Component Submissions ({studentSubmissions.length})
                                    </h4>
                                    
                                    {studentSubmissions.length === 0 ? (
                                      <div style={{ 
                                        textAlign: 'center', 
                                        color: '#6c757d', 
                                        fontStyle: 'italic',
                                        padding: '20px'
                                      }}>
                                        No component submissions found
                                      </div>
                                    ) : (
                                      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                        {studentSubmissions.map(submission => {
                                          const component = courseComponents[selectedCourse.id]?.find(comp => comp.id === submission.component);
                                          const subHours = getSubmissionHoursEarnedDetail(submission);
                                          const isAssignment = component?.type === 'Assignment';
                                          const rowClass = [
                                            'submission-row-card',
                                            submission.status === 'submitted' ? 'submission-row--submitted' : '',
                                            isAssignment ? 'submission-row--clickable' : ''
                                          ].filter(Boolean).join(' ');

                                          return (
                                            <div
                                              key={submission.id}
                                              className={rowClass}
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                if (isAssignment) {
                                                  handleComponentSubmissionClick(submission.id);
                                                }
                                              }}
                                            >
                                              <div className="submission-row-main">
                                                <div className="submission-row-heading">
                                                  <span className="submission-component-title">
                                                    {component?.title || 'Unknown Component'}
                                                  </span>
                                                  <span className="submission-type-badge">
                                                    {component?.type || 'Unknown Type'}
                                                  </span>
                                                </div>
                                                <div className="submission-meta-row">
                                                  {subHours.totalHours > 0 && (
                                                    <div className="submission-hours-chips">
                                                      <span className="course-hours-chip course-hours-total">
                                                        Total - {subHours.totalHours}
                                                      </span>
                                                      {subHours.hourEntries.map(([t, v]) => (
                                                        <span key={t} className="course-hours-chip">
                                                          {t} - {Math.round(v * 10) / 10}
                                                        </span>
                                                      ))}
                                                    </div>
                                                  )}
                                                  <span className="submission-submitted-at">
                                                    Submitted {formatDate(submission.createddate)}
                                                  </span>
                                                </div>
                                              </div>

                                              <div className="submission-row-badges">
                                                {submission.status === 'submitted' && component?.type === 'Assignment' && (
                                                  <span className="submission-status-badge submission-status-badge--review">
                                                    Requires Review
                                                  </span>
                                                )}
                                                {submission.status === 'submitted' && component?.type === 'Discussion Board' && (
                                                  <span className="submission-status-badge submission-status-badge--partial">
                                                    Partially Complete
                                                  </span>
                                                )}
                                                {!(submission.status === 'submitted' && (component?.type === 'Assignment' || component?.type === 'Discussion Board')) && (
                                                  <span
                                                    className={`submission-status-badge submission-status-badge--${submission.status === 'submitted' ? 'pending' : submission.status === 'completed' ? 'done' : 'neutral'}`}
                                                  >
                                                    {submission.status}
                                                  </span>
                                                )}
                                                {isAssignment && (
                                                  <span
                                                    className="submission-open-hint"
                                                    title="Opens grading in a new browser tab"
                                                  >
                                                    <svg
                                                      xmlns="http://www.w3.org/2000/svg"
                                                      width="12"
                                                      height="12"
                                                      viewBox="0 0 24 24"
                                                      fill="none"
                                                      stroke="currentColor"
                                                      strokeWidth="2"
                                                      strokeLinecap="round"
                                                      strokeLinejoin="round"
                                                      aria-hidden={true}
                                                    >
                                                      <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                                                      <polyline points="15 3 21 3 21 9" />
                                                      <line x1="10" y1="14" x2="21" y2="3" />
                                                    </svg>
                                                    <span>New tab</span>
                                                  </span>
                                                )}
                                              </div>
                                            </div>
                                          );
                                        })}
                                      </div>
                                    )}
                                  </div>
                                </td>
                              </tr>
                            )}
                            </React.Fragment>
                          );
                        });
                      })()}
                    </tbody>
                  </table>
                </div>
                );
              })()}
            </div>
          </div>
        </div>
      )}

      {/* Add Student Modal */}
      {showAddStudentModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: '#fff',
            padding: '2rem',
            borderRadius: 8,
            maxWidth: 600,
            width: '90%',
            maxHeight: '90vh',
            overflow: 'auto',
            position: 'relative'
          }}>
            <button
              onClick={() => {
                setShowAddStudentModal(false);
                setSelectedStudentsToAdd([]);
                setStudentSearchTerm('');
                setSelectedCourseGroup('');
                setAddStudentStartDate('');
                setAddStudentEndDate('');
              }}
              style={{
                position: 'absolute',
                top: '12px',
                left: '12px',
                background: 'none',
                border: 'none',
                fontSize: '24px',
                color: '#6b7280',
                cursor: 'pointer',
                padding: '4px',
                borderRadius: '4px',
                transition: 'all 0.2s ease',
                lineHeight: 1,
                width: '32px',
                height: '32px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = '#e5e7eb';
                e.target.style.color = '#374151';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = 'transparent';
                e.target.style.color = '#6b7280';
              }}
              title="Close"
            >
              ×
            </button>
            <h2 style={{ marginTop: 0, marginBottom: 24 }}>Add Students to "{selectedCourse?.title}"</h2>
            {selectedCourse?.modlock !== true && (
              <div style={{ marginBottom: 16, padding: 12, backgroundColor: '#fff3cd', border: '1px solid #ffc107', borderRadius: 6, color: '#856404' }}>
                Enrollment is not allowed for this course because it is not locked for modifications.
              </div>
            )}
            
            {addStudentsSingleCourseMode ? (
              <>
                <div style={{ marginBottom: 16, padding: 12, backgroundColor: '#eff6ff', border: '1px solid #93c5fd', borderRadius: 6, color: '#1e3a5f', fontSize: 14, lineHeight: 1.5 }}>
                  This general course is not in a course group. Students are enrolled in this course only. Optionally set start and end dates for the enrollment.
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 12, marginBottom: 16 }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: 8, fontWeight: 600 }}>
                      Start date <span style={{ color: '#6b7280', fontWeight: 500 }}>(optional)</span>
                    </label>
                    <input
                      type="date"
                      value={addStudentStartDate}
                      onChange={(e) => setAddStudentStartDate(e.target.value)}
                      style={{ width: '100%', padding: '0.5rem', borderRadius: 6, border: '1px solid #ccc', boxSizing: 'border-box' }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: 8, fontWeight: 600 }}>
                      End date <span style={{ color: '#6b7280', fontWeight: 500 }}>(optional)</span>
                    </label>
                    <input
                      type="date"
                      value={addStudentEndDate}
                      onChange={(e) => setAddStudentEndDate(e.target.value)}
                      style={{ width: '100%', padding: '0.5rem', borderRadius: 6, border: '1px solid #ccc', boxSizing: 'border-box' }}
                    />
                  </div>
                </div>
              </>
            ) : (
              <>
                {selectedCourseGroup && (() => {
                  const selectedGroup = courseGroups.find(g => g.id === selectedCourseGroup);
                  const hasUnlocked = selectedGroup && groupHasUnlockedCourses(selectedGroup);
                  return hasUnlocked ? (
                    <div style={{ marginBottom: 16, padding: 12, backgroundColor: '#fff3cd', border: '1px solid #ffc107', borderRadius: 6, color: '#856404' }}>
                      Enrollment is not allowed for one or more courses in this group because they are not locked for modifications.
                    </div>
                  ) : null;
                })()}
                <div style={{ marginBottom: 16 }}>
                  <label style={{ display: 'block', marginBottom: 8, fontWeight: 600 }}>
                    Course Group <span style={{ color: '#dc2626' }}>*</span>:
                  </label>
                  <select
                    value={selectedCourseGroup}
                    onChange={(e) => setSelectedCourseGroup(e.target.value)}
                    required
                    style={{ width: '100%', padding: '0.5rem', borderRadius: 6, border: '1px solid #ccc' }}
                  >
                    <option value="">Select a course group</option>
                    {courseGroups
                      .filter(group => group.courses && group.courses.includes(selectedCourse?.id))
                      .map(group => (
                        <option key={group.id} value={group.id}>
                          {group.title}
                        </option>
                      ))}
                  </select>
                </div>

                {selectedCourseGroup && (() => {
                  const selectedGroup = courseGroups.find(group => group.id === selectedCourseGroup);
                  if (selectedGroup && selectedGroup.setup && Array.isArray(selectedGroup.setup) && selectedGroup.setup.length > 0) {
                    return (
                      <div style={{ marginBottom: 16 }}>
                        <div style={{
                          fontSize: '12px',
                          fontWeight: '600',
                          color: '#374151',
                          marginBottom: '8px'
                        }}>
                          Wheel ({selectedGroup.title})
                        </div>
                        <div style={{
                          display: 'grid',
                          gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))',
                          gap: '8px'
                        }}>
                          {selectedGroup.setup.map((setupItem, index) => {
                            const course = courses.find(c => c.id === setupItem.course_id);
                            const courseTitle = course?.title || setupItem.course_id;
                            const isCurrentCourse = setupItem.course_id === selectedCourse?.id;

                            return (
                              <div
                                key={index}
                                style={{
                                  display: 'flex',
                                  flexDirection: 'column',
                                  padding: '10px',
                                  backgroundColor: isCurrentCourse ? '#eff6ff' : '#f9fafb',
                                  borderRadius: '6px',
                                  border: isCurrentCourse ? '1px solid #3b82f6' : '1px solid #e5e7eb',
                                  width: '100%',
                                  boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)'
                                }}
                              >
                                <div style={{
                                  fontSize: '11px',
                                  fontWeight: '600',
                                  color: '#374151',
                                  marginBottom: '6px',
                                  lineHeight: '1.3'
                                }}>
                                  {courseTitle}
                                </div>
                                <div style={{ fontSize: '10px', color: '#6b7280', marginBottom: '4px' }}>
                                  <div>Start: {setupItem.start_date || 'N/A'}</div>
                                  {setupItem.end_date && (
                                    <div>End: {setupItem.end_date}</div>
                                  )}
                                </div>
                                {isCurrentCourse && (
                                  <span style={{
                                    fontSize: '9px',
                                    fontWeight: '600',
                                    color: '#3b82f6',
                                    backgroundColor: '#dbeafe',
                                    padding: '2px 6px',
                                    borderRadius: '4px',
                                    alignSelf: 'flex-start',
                                    marginTop: '4px'
                                  }}>
                                    Current
                                  </span>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  }
                  return null;
                })()}
              </>
            )}

            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', marginBottom: 8, fontWeight: 600 }}>Search Students:</label>
              <textarea
                placeholder="Search by name or email...&#10;You can also paste multiple emails separated by lines:&#10;student1@example.com&#10;student2@example.com"
                value={studentSearchTerm}
                onChange={(e) => setStudentSearchTerm(e.target.value)}
                rows={3}
                style={{ 
                  width: '100%', 
                  padding: '0.5rem 0.75rem', 
                  borderRadius: 6, 
                  border: '1px solid #ccc', 
                  marginBottom: 12,
                  resize: 'vertical',
                  fontFamily: 'inherit'
                }}
              />
              
              {(() => {
                const searchTerms = studentSearchTerm.trim();
                if (!searchTerms) {
                  // No search term - show all available students
                  const enrolledStudentIds = students.map(s => s.enrollment_id);
                  const availableStudents = allStudents.filter(s => !enrolledStudentIds.includes(s.enrollment_id));
                  
                  return (
                    <div style={{ 
                      border: '1px solid #e0e0e0', 
                      borderRadius: 6, 
                      padding: 16, 
                      maxHeight: 300, 
                      overflow: 'auto',
                      backgroundColor: '#f8f9fa'
                    }}>
                      {availableStudents.length === 0 ? (
                        <div style={{ textAlign: 'center', color: '#666', padding: 20 }}>
                          No available students to add
                        </div>
                      ) : (
                        availableStudents.map(student => {
                          const isSelected = selectedStudentsToAdd.includes(student.enrollment_id);
                          return (
                            <div 
                              key={student.enrollment_id} 
                              onClick={() => toggleStudentSelection(student.enrollment_id)}
                              style={{ 
                                display: 'flex', 
                                alignItems: 'center', 
                                padding: '8px 12px',
                                borderBottom: '1px solid #e0e0e0',
                                cursor: 'pointer',
                                backgroundColor: isSelected ? '#e3f2fd' : 'transparent',
                                transition: 'background-color 0.2s ease'
                              }}
                              onMouseEnter={(e) => {
                                if (!isSelected) {
                                  e.target.style.backgroundColor = '#f8f9fa';
                                }
                              }}
                              onMouseLeave={(e) => {
                                if (!isSelected) {
                                  e.target.style.backgroundColor = 'transparent';
                                }
                              }}
                            >
                              <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={() => toggleStudentSelection(student.enrollment_id)}
                                onClick={(e) => e.stopPropagation()}
                                style={{ marginRight: 12 }}
                              />
                              <div>
                                <strong>{student.first_name} {student.last_name}</strong>
                                <br />
                                <small style={{ color: '#666' }}>{student.email}</small>
                              </div>
                            </div>
                          );
                        })
                      )}
                    </div>
                  );
                }
                
                // Split search terms by newlines, commas, semicolons, or spaces
                const searchLines = searchTerms
                  .split(/[\n,;]+/)
                  .map(term => term.trim().toLowerCase())
                  .filter(term => term.length > 0);
                
                const filtered = allStudents.filter(s => {
                  // Create searchable text from student data
                  const searchableText = [
                    s.first_name || '',
                    s.last_name || '',
                    s.email || ''
                  ].join(' ').toLowerCase();
                  
                  // Check if any search term matches
                  return searchLines.some(term => searchableText.includes(term));
                });
                
                // Filter out students already enrolled in this course (check both students array and courseEnrollments for active enrollments)
                const enrolledStudentIds = students.map(s => s.enrollment_id);
                // Also check courseEnrollments for students with active enrollments in this course
                const enrolledFromCourseEnrollments = courseEnrollments
                  .filter(ce => ce.course === selectedCourse?.id && ce.status === 'enrolled')
                  .map(ce => ce.enrollment);
                const allEnrolledIds = new Set([...enrolledStudentIds, ...enrolledFromCourseEnrollments]);
                const availableStudents = filtered.filter(s => !allEnrolledIds.has(s.enrollment_id));
                
                return (
                  <div style={{ 
                    border: '1px solid #e0e0e0', 
                    borderRadius: 6, 
                    padding: 16, 
                    maxHeight: 300, 
                    overflow: 'auto',
                    backgroundColor: '#f8f9fa'
                  }}>
                    {availableStudents.length === 0 ? (
                      <div style={{ textAlign: 'center', color: '#666', padding: 20 }}>
                        {searchTerms ? 'No students found matching your search' : 'No available students to add'}
                      </div>
                    ) : (
                      availableStudents.map(student => {
                        const isSelected = selectedStudentsToAdd.includes(student.enrollment_id);
                        return (
                          <div 
                            key={student.enrollment_id} 
                            onClick={() => toggleStudentSelection(student.enrollment_id)}
                            style={{ 
                              display: 'flex', 
                              alignItems: 'center', 
                              padding: '8px 12px',
                              borderBottom: '1px solid #e0e0e0',
                              cursor: 'pointer',
                              backgroundColor: isSelected ? '#e3f2fd' : 'transparent',
                              transition: 'background-color 0.2s ease'
                            }}
                            onMouseEnter={(e) => {
                              if (!isSelected) {
                                e.target.style.backgroundColor = '#f8f9fa';
                              }
                            }}
                            onMouseLeave={(e) => {
                              if (!isSelected) {
                                e.target.style.backgroundColor = 'transparent';
                              }
                            }}
                          >
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => toggleStudentSelection(student.enrollment_id)}
                              onClick={(e) => e.stopPropagation()}
                              style={{ marginRight: 12 }}
                            />
                            <div>
                              <strong>{student.first_name} {student.last_name}</strong>
                              <br />
                              <small style={{ color: '#666' }}>{student.email}</small>
                            </div>
                          </div>
                        );
                      })
                    )}
                  </div>
                );
              })()}
              
              <div style={{ marginTop: 8, fontSize: 14, color: '#666' }}>
                {selectedStudentsToAdd.length} student(s) selected
              </div>
            </div>

            <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
              <button
                onClick={() => {
                  setShowAddStudentModal(false);
                  setSelectedStudentsToAdd([]);
                  setStudentSearchTerm('');
                  setSelectedCourseGroup('');
                  setAddStudentStartDate('');
                  setAddStudentEndDate('');
                }}
                style={{
                  padding: '0.5rem 1rem',
                  borderRadius: 6,
                  border: '1px solid #ccc',
                  background: '#fff',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              {(() => {
                const selectedGroup = courseGroups.find(g => g.id === selectedCourseGroup);
                const addDisabled = addStudentsSingleCourseMode
                  ? selectedStudentsToAdd.length === 0 || selectedCourse?.modlock !== true
                  : selectedStudentsToAdd.length === 0 ||
                    !selectedCourseGroup ||
                    selectedCourse?.modlock !== true ||
                    (selectedGroup && groupHasUnlockedCourses(selectedGroup));
                return (
                  <button
                    onClick={handleAddStudents}
                    disabled={addDisabled}
                    style={{
                      padding: '0.5rem 1rem',
                      borderRadius: 6,
                      border: '1px solid #1D4E89',
                      background: addDisabled ? '#ccc' : '#1D4E89',
                      color: '#fff',
                      cursor: addDisabled ? 'not-allowed' : 'pointer'
                    }}
                  >
                    Add {selectedStudentsToAdd.length} Student(s)
                  </button>
                );
              })()}
            </div>
          </div>
        </div>
      )}

      {/* Copy Roster Modal */}
      {showCopyRosterModal && selectedCourse && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: '#fff',
            padding: '2rem',
            borderRadius: 8,
            maxWidth: 800,
            width: '90%',
            maxHeight: '90vh',
            overflow: 'auto'
          }}>
            <h2 style={{ marginTop: 0, marginBottom: 24 }}>Copy Roster from "{selectedCourse?.title}"</h2>
            
            {/* Target Course Selection */}
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', marginBottom: 8, fontWeight: 600 }}>What course would you like to copy roster to?</label>
              <select
                value={targetCourseId}
                onChange={(e) => setTargetCourseId(e.target.value)}
                required
                style={{ width: '100%', padding: '0.5rem', borderRadius: 6, border: '1px solid #ccc' }}
              >
                <option value="">Select a target course</option>
                {(() => {
                  // Only show courses that allow enrollment (modlock === true), exclude current course
                  const availableCourses = enrollableCourses
                    .filter(course => course.id !== selectedCourse.id)
                    .sort((a, b) => (a.title || '').localeCompare(b.title || ''));
                  
                  return availableCourses.map(course => (
                    <option key={course.id} value={course.id}>
                      {course.title}
                    </option>
                  ));
                })()}
              </select>
            </div>

            {/* Date Selection */}
            <div style={{ display: 'flex', gap: 16, marginBottom: 16 }}>
              <div style={{ flex: 1 }}>
                <label style={{ display: 'block', marginBottom: 8, fontWeight: 600 }}>Cohort Date (optional):</label>
                <input
                  type="date"
                  value={copyStartDate}
                  onChange={(e) => setCopyStartDate(e.target.value)}
                  style={{ width: '100%', padding: '0.5rem', borderRadius: 6, border: '1px solid #ccc' }}
                />
              </div>
              <div style={{ flex: 1 }}>
                <label style={{ display: 'block', marginBottom: 8, fontWeight: 600 }}>End Date (optional):</label>
                <input
                  type="date"
                  value={copyEndDate}
                  onChange={(e) => setCopyEndDate(e.target.value)}
                  style={{ width: '100%', padding: '0.5rem', borderRadius: 6, border: '1px solid #ccc' }}
                />
              </div>
            </div>

            {/* Previous Course Checkbox */}
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={setPreviousCourse}
                  onChange={(e) => setSetPreviousCourse(e.target.checked)}
                  style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                />
                <span style={{ fontWeight: 500 }}>
                  Set "{selectedCourse?.title}" as previous_course
                </span>
              </label>
            </div>

            {/* Students Checkbox Table */}
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', marginBottom: 8, fontWeight: 600 }}>Select Students to Copy:</label>
              <div style={{ 
                border: '1px solid #e0e0e0', 
                borderRadius: 6, 
                padding: 16, 
                maxHeight: 400, 
                overflow: 'auto',
                backgroundColor: '#f8f9fa'
              }}>
                {(() => {
                  const courseEnrollmentsForCourse = courseEnrollments.filter(
                    enrollment => enrollment.course === selectedCourse.id
                  );

                  if (courseEnrollmentsForCourse.length === 0) {
                    return (
                      <div style={{ textAlign: 'center', color: '#666', padding: 20 }}>
                        No students enrolled in this course
                      </div>
                    );
                  }

                  return (
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <thead>
                        <tr style={{ borderBottom: '2px solid #e0e0e0', backgroundColor: '#fff' }}>
                          <th style={{ padding: '8px', textAlign: 'left', fontWeight: 600 }}>Select</th>
                          <th style={{ padding: '8px', textAlign: 'left', fontWeight: 600 }}>Name</th>
                          <th style={{ padding: '8px', textAlign: 'left', fontWeight: 600 }}>Previous Course</th>
                        </tr>
                      </thead>
                      <tbody>
                        {courseEnrollmentsForCourse.map(enrollment => {
                          const isSelected = selectedStudentsToCopy.includes(enrollment.id);
                          return (
                            <tr 
                              key={enrollment.id}
                              style={{ 
                                borderBottom: '1px solid #e0e0e0',
                                backgroundColor: isSelected ? '#e3f2fd' : '#fff',
                                cursor: 'pointer',
                                transition: 'background-color 0.2s ease'
                              }}
                              onClick={() => toggleStudentCopySelection(enrollment.id)}
                              onMouseEnter={(e) => {
                                if (!isSelected) {
                                  e.currentTarget.style.backgroundColor = '#f8f9fa';
                                }
                              }}
                              onMouseLeave={(e) => {
                                if (!isSelected) {
                                  e.currentTarget.style.backgroundColor = '#fff';
                                }
                              }}
                            >
                              <td style={{ padding: '8px' }}>
                                <input
                                  type="checkbox"
                                  checked={isSelected}
                                  onChange={() => toggleStudentCopySelection(enrollment.id)}
                                  onClick={(e) => e.stopPropagation()}
                                />
                              </td>
                              <td style={{ padding: '8px' }}>
                                {enrollment.first_name} {enrollment.last_name}
                              </td>
                              <td style={{ padding: '8px', color: '#666' }}>
                                {enrollment.previous_course_title || 'None'}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  );
                })()}
              </div>
              <div style={{ marginTop: 8, fontSize: 14, color: '#666' }}>
                {selectedStudentsToCopy.length} student(s) selected
              </div>
            </div>

            <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
              <button
                onClick={() => {
                  setShowCopyRosterModal(false);
                  setSelectedStudentsToCopy([]);
                  setTargetCourseId('');
                  setCopyStartDate(getCurrentDate());
                  setCopyEndDate('');
                  setSetPreviousCourse(true);
                }}
                style={{
                  padding: '0.5rem 1rem',
                  borderRadius: 6,
                  border: '1px solid #ccc',
                  background: '#fff',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleCopyRoster}
                disabled={selectedStudentsToCopy.length === 0 || !targetCourseId}
                style={{
                  padding: '0.5rem 1rem',
                  borderRadius: 6,
                  border: '1px solid #28a745',
                  background: (selectedStudentsToCopy.length === 0 || !targetCourseId) ? '#ccc' : '#28a745',
                  color: '#fff',
                  cursor: (selectedStudentsToCopy.length === 0 || !targetCourseId) ? 'not-allowed' : 'pointer'
                }}
              >
                Copy {selectedStudentsToCopy.length} Student(s)
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Withdrawal Modal */}
      {showWithdrawalModal && studentToWithdraw && selectedCourse && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10000
        }}>
          <div style={{
            backgroundColor: '#fff',
            borderRadius: '12px',
            padding: '24px',
            maxWidth: '600px',
            width: '90%',
            maxHeight: '90vh',
            overflow: 'auto',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h2 style={{ margin: 0, fontSize: '20px', fontWeight: '600', color: '#111827' }}>
                Withdraw Student
              </h2>
              <button
                onClick={() => {
                  setShowWithdrawalModal(false);
                  setStudentToWithdraw(null);
                }}
                style={{
                  background: 'transparent',
                  border: 'none',
                  fontSize: '24px',
                  cursor: 'pointer',
                  color: '#6b7280',
                  padding: '4px',
                  lineHeight: 1
                }}
                onMouseEnter={(e) => { e.target.style.color = '#374151'; }}
                onMouseLeave={(e) => { e.target.style.color = '#6b7280'; }}
              >
                ×
              </button>
            </div>

            <div style={{ marginBottom: '20px' }}>
              <p style={{ fontSize: '16px', color: '#374151', marginBottom: '16px' }}>
                Do you want to withdraw {studentToWithdraw.first_name} {studentToWithdraw.last_name} from this course?
              </p>

              {studentToWithdraw.coursegroup_id && (() => {
                const studentCourseGroup = courseGroups.find(group => group.id === studentToWithdraw.coursegroup_id);
                if (studentCourseGroup && studentCourseGroup.setup && Array.isArray(studentCourseGroup.setup) && studentCourseGroup.setup.length > 0) {
                  return (
                    <div style={{ marginTop: '16px' }}>
                      <p style={{ 
                        fontSize: '14px', 
                        fontWeight: '600', 
                        color: '#dc2626', 
                        marginBottom: '12px' 
                      }}>
                        NOTE: Student will be withdrawn from all these courses as well:
                      </p>
                      <div style={{ 
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))',
                        gap: '8px',
                        marginTop: '8px'
                      }}>
                        {studentCourseGroup.setup.map((setupItem, index) => {
                          const course = courses.find(c => c.id === setupItem.course_id);
                          const courseTitle = course?.title || setupItem.course_id;
                          const isCurrentCourse = setupItem.course_id === selectedCourse?.id;
                          
                          return (
                            <div
                              key={index}
                              style={{
                                display: 'flex',
                                flexDirection: 'column',
                                padding: '10px',
                                backgroundColor: isCurrentCourse ? '#eff6ff' : '#f9fafb',
                                borderRadius: '6px',
                                border: isCurrentCourse ? '1px solid #3b82f6' : '1px solid #e5e7eb',
                                width: '100%',
                                boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)'
                              }}
                            >
                              <div style={{ 
                                fontSize: '11px', 
                                fontWeight: '600', 
                                color: '#374151',
                                marginBottom: '6px',
                                lineHeight: '1.3'
                              }}>
                                {courseTitle}
                              </div>
                              <div style={{ fontSize: '10px', color: '#6b7280', marginBottom: '4px' }}>
                                <div>Start: {setupItem.start_date || 'N/A'}</div>
                                {setupItem.end_date && (
                                  <div>End: {setupItem.end_date}</div>
                                )}
                              </div>
                              {isCurrentCourse && (
                                <span style={{
                                  fontSize: '9px',
                                  fontWeight: '600',
                                  color: '#3b82f6',
                                  backgroundColor: '#dbeafe',
                                  padding: '2px 6px',
                                  borderRadius: '4px',
                                  alignSelf: 'flex-start',
                                  marginTop: '4px'
                                }}>
                                  Current
                                </span>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                }
                return null;
              })()}
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '24px' }}>
              <button
                onClick={() => {
                  setShowWithdrawalModal(false);
                  setStudentToWithdraw(null);
                }}
                style={{
                  padding: '10px 20px',
                  borderRadius: '6px',
                  border: '1px solid #d1d5db',
                  background: '#fff',
                  color: '#374151',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500'
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = '#f9fafb';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = '#fff';
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleWithdrawEnrollment}
                style={{
                  padding: '10px 20px',
                  borderRadius: '6px',
                  border: 'none',
                  background: '#eab308',
                  color: '#fff',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500'
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = '#ca8a04';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = '#eab308';
                }}
              >
                Withdraw Student
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Deployment Flag Container - memoized, CSS show/hide for instant expand (like StudentHoursOverview) */}
      <FlagContainer
        flaggedCount={flaggedCount}
        flaggedCoursesWithStatus={flaggedCoursesWithStatus}
        canOpenCourseBuilder={canOpenCourseBuilder}
      />
    </div>
  );
};

export default MyCoursesManager;
